import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { 
  Briefcase, 
  Plus, 
  Search, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  User, 
  Calendar, 
  X, 
  Edit2, 
  Trash2, 
  ChevronLeft, 
  ChevronRight, 
  ShieldCheck, 
  DollarSign,
  TrendingUp,
  Percent,
  Check,
  Building,
  RefreshCw,
  SlidersHorizontal,
  ExternalLink,
  Wallet,
  CreditCard,
  Info,
  Users,
  ArrowRightLeft
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { PaginationFooter } from "../../components/common/PaginationFooter";
import { useAuthState } from "react-redux-django-auth";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  useStaffJobsQuery,
  useAgentsQuery,
  useServiceRatesQuery,
  useMonthlyPayoutSummaryQuery,
} from "../../hooks/queries/useJobFinanceQueries";
import { 
  fetchJobs, 
  fetchJobsPaginated,
  FetchJobsParams,
  fetchAgents, 
  fetchServiceRates,
  createServiceRate,
  updateServiceRate,
  deleteServiceRate,
  createJob, 
  updateJob, 
  deleteJob, 
  toggleCommissionApproval, 
  toggleCommissionPaid,
  handoffDeliverable,
  updateJobCost,
  createAgent,
  fetchMonthlyPayoutSummary,
  disburseMonthlyCommission,
  Job, 
  Agent, 
  ServiceRate,
  ServiceType, 
  JobStatus,
  SuperAdminJobAnalytics,
  PaymentStatus,
  MonthlyStaffPayout,
  MonthlyPayoutSummary
} from "../../services/jobService";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);



const SERVICE_TYPES: { label: string; value: ServiceType }[] = [
  { label: "NERD", value: "NERD" },
  { label: "Affidavit", value: "AFFIDAVIT" },
  { label: "CAC Registration", value: "CAC" },
  { label: "TIN Registration", value: "TIN" },
  { label: "Web Design", value: "WEB_DESIGN" },
  { label: "Other Service", value: "OTHER" },
];

type MetricInfoKey = "PENDING_JOBS" | "GROSS_REVENUE" | "TOTAL_COSTS" | "COMMISSIONS" | "NET_PROFIT";

const SuperAdminStaffJobs: React.FC = () => {
  const { user } = useAuthState();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMetricInfo, setSelectedMetricInfo] = useState<MetricInfoKey | null>(null);

  // Active View Tab: 'jobs' | 'payouts' | 'agents' | 'services'
  const [activeTab, setActiveTab] = useState<"jobs" | "payouts" | "agents" | "services">("jobs");

  // Services & Rates Management
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedServiceForEdit, setSelectedServiceForEdit] = useState<ServiceRate | null>(null);
  const [serviceFormName, setServiceFormName] = useState("");
  const [serviceFormCode, setServiceFormCode] = useState("");
  const [serviceFormBaseCommission, setServiceFormBaseCommission] = useState("0");
  const [serviceFormDefaultPrice, setServiceFormDefaultPrice] = useState("0");
  const [serviceFormDefaultCost, setServiceFormDefaultCost] = useState("0");
  const [serviceFormOrder, setServiceFormOrder] = useState("1");
  const [serviceFormIsActive, setServiceFormIsActive] = useState(true);
  const [serviceSubmitting, setServiceSubmitting] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ServiceRate | null>(null);
  const [serviceDeleteSubmitting, setServiceDeleteSubmitting] = useState(false);

  // Filtering & Search
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10) || 1;
  const pageSize = parseInt(searchParams.get("page_size") || "20", 10) || 20;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const loadRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (currentPage !== 1) {
        updateQueryParams({ page: 1 });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDebouncedSearch(searchQuery);
      if (currentPage !== 1) {
        updateQueryParams({ page: 1 });
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    if (currentPage !== 1) {
      updateQueryParams({ page: 1 });
    }
  };

  const updateQueryParams = (newParams: { page?: number; page_size?: number }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newParams.page !== undefined) next.set("page", String(newParams.page));
      if (newParams.page_size !== undefined) next.set("page_size", String(newParams.page_size));
      return next;
    }, { replace: true });
  };

  const handlePageChange = (newPage: number) => {
    updateQueryParams({ page: newPage });
  };

  const handlePageSizeChange = (newSize: number) => {
    updateQueryParams({ page: 1, page_size: newSize });
  };

  const [staffFilter, setStaffFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [approvalFilter, setApprovalFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [monthFilter, setMonthFilter] = useState("ALL");
  const [payoutFilter, setPayoutFilter] = useState("ALL");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("ALL");
  const [referralFilter, setReferralFilter] = useState("ALL");
  const [agentFilter, setAgentFilter] = useState("ALL");
  const [knownStaffMembers, setKnownStaffMembers] = useState<{ id: string; name: string }[]>([]);

  // Memoized Query Parameters
  const queryParams = useMemo<FetchJobsParams>(() => ({
    month: monthFilter !== "ALL" ? monthFilter : undefined,
    page: currentPage,
    pageSize: pageSize,
    search: debouncedSearch.trim() || undefined,
    staff: staffFilter !== "ALL" ? staffFilter : undefined,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    job_type: typeFilter !== "ALL" ? typeFilter : undefined,
    referral_source: referralFilter !== "ALL" ? referralFilter : undefined,
    agent: agentFilter !== "ALL" ? agentFilter : undefined,
    payout: payoutFilter !== "ALL" ? payoutFilter : undefined,
    approval: approvalFilter !== "ALL" ? approvalFilter : undefined,
    payment_status: paymentStatusFilter !== "ALL" ? paymentStatusFilter : undefined,
  }), [
    monthFilter,
    currentPage,
    pageSize,
    debouncedSearch,
    staffFilter,
    statusFilter,
    typeFilter,
    referralFilter,
    agentFilter,
    payoutFilter,
    approvalFilter,
    paymentStatusFilter,
  ]);

  // Queries with cost-saving global caching (staleTime: 3 min)
  const {
    data: jobsData,
    isLoading: isJobsLoading,
    isFetching: isJobsFetching,
    refetch: refetchJobs,
  } = useStaffJobsQuery(queryParams);

  const serverJobs = useMemo(
    () => (Array.isArray(jobsData?.data) ? jobsData.data : []),
    [jobsData?.data]
  );
  const [jobs, setJobs] = useState<Job[]>([]);
  useEffect(() => {
    setJobs(serverJobs);
  }, [serverJobs]);

  const totalCount = jobsData?.count || 0;
  const serverAnalytics = jobsData?.analytics || null;
  const loading = isJobsLoading && !jobsData;
  const tableLoading = isJobsFetching;

  const { data: agentsData = [], refetch: refetchAgents } = useAgentsQuery();
  const serverAgents = useMemo(
    () => (Array.isArray(agentsData) ? agentsData : []),
    [agentsData]
  );
  const [agents, setAgents] = useState<Agent[]>([]);
  useEffect(() => {
    setAgents(serverAgents);
  }, [serverAgents]);

  const { data: serviceRatesData = [], refetch: refetchServiceRates } = useServiceRatesQuery(true);
  const serverServiceRates = useMemo(
    () => (Array.isArray(serviceRatesData) ? serviceRatesData : []),
    [serviceRatesData]
  );
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  useEffect(() => {
    setServiceRates(serverServiceRates);
  }, [serverServiceRates]);

  // Monthly Payout Tab States & Query
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  const [selectedPayoutMonth, setSelectedPayoutMonth] = useState(currentMonthStr);
  const [payoutSummary, setPayoutSummary] = useState<MonthlyPayoutSummary | null>(null);

  const {
    data: payoutSummaryData,
    isLoading: isPayoutLoading,
    refetch: refetchPayoutSummary,
  } = useMonthlyPayoutSummaryQuery(selectedPayoutMonth, activeTab === "payouts");
  const loadingPayoutSummary = isPayoutLoading && !payoutSummaryData;

  useEffect(() => {
    if (payoutSummaryData) {
      setPayoutSummary(payoutSummaryData);
    }
  }, [payoutSummaryData]);

  const dynamicServiceTypes = useMemo(() => {
    if (serviceRates && serviceRates.length > 0) {
      return serviceRates
        .filter((sr) => sr.is_active)
        .map((sr) => ({ label: sr.name, value: sr.code }));
    }
    return SERVICE_TYPES;
  }, [serviceRates]);

  // SuperAdmin Log Job Modal (Full Access)
  const [isLogJobModalOpen, setIsLogJobModalOpen] = useState(false);
  const [logJobSubmitting, setLogJobSubmitting] = useState(false);
  const [logJobError, setLogJobError] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split("T")[0];
  const [logJobData, setLogJobData] = useState({
    date: todayStr,
    customer_name: "",
    job_type: "NERD" as ServiceType,
    referral_source: "DIRECT" as "WALK_IN" | "STAFF" | "DIRECT" | "AGENT",
    agent_profile: "" as string,
    assigned_staff: "" as string,
    amount_paid: "0",
    payment_status: "PAID" as PaymentStatus,
    job_cost: "0",
    status: "PENDING" as JobStatus,
    is_approved: true,
    is_commission_paid: false,
    remark: "",
  });

  // Edit Financials Modal (Amount Paid / Payment Status / Job Cost / Status / Approval / Paid Out)
  const [selectedJobForEdit, setSelectedJobForEdit] = useState<Job | null>(null);
  const [editStatus, setEditStatus] = useState<JobStatus>("PENDING");
  const [editPaymentStatus, setEditPaymentStatus] = useState<PaymentStatus>("PAID");
  const [editAmountPaid, setEditAmountPaid] = useState("");
  const [editJobCost, setEditJobCost] = useState("");
  const [editIsApproved, setEditIsApproved] = useState(false);
  const [editIsCommissionPaid, setEditIsCommissionPaid] = useState(false);
  const [editReferralSource, setEditReferralSource] = useState<string>("DIRECT");
  const [editAgentProfileId, setEditAgentProfileId] = useState<string>("");
  const [editCommission, setEditCommission] = useState("");
  const [costSubmitting, setCostSubmitting] = useState(false);

  const defaultServiceRateCommission = useMemo(() => {
    if (!selectedJobForEdit) return 0;
    const sr = serviceRates.find((s) => s.code === selectedJobForEdit.job_type);
    if (sr && sr.base_commission) {
      return parseFloat(sr.base_commission) || 0;
    }
    if (selectedJobForEdit.job_type === "NERD" || selectedJobForEdit.job_type === "AFFIDAVIT") {
      return 500;
    }
    return parseFloat(selectedJobForEdit.expected_commission || "0") || 0;
  }, [selectedJobForEdit, serviceRates]);

  const defaultServiceRateCost = useMemo(() => {
    if (!selectedJobForEdit) return 0;
    const sr = serviceRates.find((s) => s.code === selectedJobForEdit.job_type);
    if (sr && sr.default_cost) {
      return parseFloat(String(sr.default_cost)) || 0;
    }
    return 0;
  }, [selectedJobForEdit, serviceRates]);

  const activeAgent = useMemo(() => {
    if (!editAgentProfileId) return null;
    return agents.find((a) => String(a.id) === String(editAgentProfileId)) || null;
  }, [editAgentProfileId, agents]);

  const activeAgentTier = useMemo(() => {
    if (!activeAgent || !selectedJobForEdit) return null;
    return activeAgent.price_tiers?.find((t) => t.service_type === selectedJobForEdit.job_type) || null;
  }, [activeAgent, selectedJobForEdit]);

  // Monthly Payout Disburse Modal States
  const [disburseModalStaff, setDisburseModalStaff] = useState<MonthlyStaffPayout | null>(null);
  const [disbursing, setDisbursing] = useState(false);

  // Add Agent Modal
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [newAgentName, setNewAgentName] = useState("");
  const [newAgentPhone, setNewAgentPhone] = useState("");
  const [newAgentEmail, setNewAgentEmail] = useState("");
  const [agentSubmitting, setAgentSubmitting] = useState(false);

  // Multi-Staff Handoff & Split State (SuperAdmin)
  const [isHandoffModalOpen, setIsHandoffModalOpen] = useState(false);
  const [handoffTargetJob, setHandoffTargetJob] = useState<Job | null>(null);
  const [handoffSuccessorId, setHandoffSuccessorId] = useState<string>("");
  const [handoffPredecessorSplit, setHandoffPredecessorSplit] = useState<number>(50);
  const [handoffSuccessorSplit, setHandoffSuccessorSplit] = useState<number>(50);
  const [handoffReason, setHandoffReason] = useState<string>("");
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [handoffSuccess, setHandoffSuccess] = useState<string | null>(null);

  const loadData = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.staffJobs.lists() });
    await queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    await queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
  };

  const loadPayoutSummary = async (monthToLoad?: string) => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.staffJobs.payoutSummary(monthToLoad || selectedPayoutMonth),
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refetchJobs(),
        refetchAgents(),
        refetchServiceRates(),
        activeTab === "payouts" ? refetchPayoutSummary() : Promise.resolve(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // Available Months for Filtering (Dynamically extracted from logged jobs and server analytics)
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    const current = new Date().toISOString().slice(0, 7);
    set.add(current);
    if ((serverAnalytics as any)?.available_months && Array.isArray((serverAnalytics as any).available_months)) {
      (serverAnalytics as any).available_months.forEach((m: string) => set.add(m));
    }
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    safeJobs.forEach((j) => {
      if (j?.date) {
        set.add(j.date.slice(0, 7));
      }
    });
    return Array.from(set).sort().reverse();
  }, [jobs, serverAnalytics]);

  // Unique Staff Members for Filtering & Job Assignment (Dynamically extracted from logged jobs + current SuperAdmin)
  const staffMembers = useMemo(() => {
    const map = new Map<string, string>();
    if (user?.id) {
      const selfName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || user.email || "SuperAdmin (Self)";
      map.set(String(user.id), `${selfName} (You)`);
    }
    knownStaffMembers.forEach((s) => map.set(s.id, s.name));
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    safeJobs.forEach((j) => {
      if (j?.assigned_staff) {
        if (!map.has(String(j.assigned_staff))) {
          map.set(
            String(j.assigned_staff),
            j.assigned_staff_name || `Staff #${j.assigned_staff}`
          );
        }
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs, user, knownStaffMembers]);

  // Keep accumulated knownStaffMembers up to date
  useEffect(() => {
    if (staffMembers.length > knownStaffMembers.length) {
      setKnownStaffMembers(staffMembers);
    }
  }, [staffMembers, knownStaffMembers.length]);

  // ── Multi-Staff Task Handoff & Commission Split Handlers (SuperAdmin) ─────
  const handleOpenHandoffModal = (job: Job) => {
    setHandoffTargetJob(job);
    setHandoffError(null);
    setHandoffSuccess(null);
    // Suggest first colleague that isn't the current assigned staff
    const otherStaff = staffMembers.find((s) => s.id !== "ALL" && s.id !== "UNASSIGNED" && String(s.id) !== String(job.assigned_staff));
    setHandoffSuccessorId(otherStaff ? String(otherStaff.id) : "");
    setHandoffPredecessorSplit(50);
    setHandoffSuccessorSplit(50);
    setHandoffReason("");
    setIsHandoffModalOpen(true);
  };

  const handleSplitPreset = (predSplit: number) => {
    setHandoffPredecessorSplit(predSplit);
    setHandoffSuccessorSplit(100 - predSplit);
  };

  const handleSubmitHandoff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handoffTargetJob) return;
    if (!handoffSuccessorId) {
      setHandoffError("Please select a successor specialist to hand off to.");
      return;
    }
    if (handoffPredecessorSplit + handoffSuccessorSplit !== 100) {
      setHandoffError("Commission split percentages must sum up to exactly 100%.");
      return;
    }

    try {
      setHandoffSubmitting(true);
      setHandoffError(null);
      const res = await handoffDeliverable(
        handoffTargetJob.id,
        {
          successor_staff_id: parseInt(handoffSuccessorId, 10),
          predecessor_split_percent: handoffPredecessorSplit,
          successor_split_percent: handoffSuccessorSplit,
          handoff_reason: handoffReason,
        }
      );

      setHandoffSuccess(res.message || "Handoff executed successfully.");
      setTimeout(() => {
        setIsHandoffModalOpen(false);
        loadData();
      }, 1200);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to execute deliverable handoff. Please try again.";
      setHandoffError(detail);
    } finally {
      setHandoffSubmitting(false);
    }
  };

  // ── Log Job Modal Handlers & Financial Calculations ──────────────────────
  const handleOpenLogJobModal = () => {
    setLogJobError(null);
    const defaultStaff = user?.id ? String(user.id) : "";
    const firstService = serviceRates.length > 0 ? serviceRates[0] : null;
    const defaultJobType = firstService ? firstService.code : "NERD";
    const defCost = firstService && firstService.default_cost !== undefined && firstService.default_cost !== null
      ? String(firstService.default_cost)
      : (defaultJobType === "NERD" ? "1500" : "0");

    setLogJobData({
      date: todayStr,
      customer_name: "",
      job_type: defaultJobType,
      referral_source: "DIRECT",
      agent_profile: "",
      assigned_staff: defaultStaff,
      amount_paid: "0",
      payment_status: "PAID",
      job_cost: defCost,
      status: "PENDING",
      is_approved: true,
      is_commission_paid: false,
      remark: "",
    });
    setIsLogJobModalOpen(true);
  };

  const handleLogJobTypeChange = (newType: string) => {
    const matchedService = serviceRates.find((s) => s.code === newType);
    let newCost = logJobData.job_cost;
    let newAmount = logJobData.amount_paid;

    if (matchedService) {
      if (matchedService.default_cost !== undefined && matchedService.default_cost !== null) {
        newCost = String(matchedService.default_cost);
      } else {
        newCost = "0.00";
      }
      if (logJobData.referral_source === "AGENT" && logJobData.agent_profile) {
        const agentObj = agents.find((a) => String(a.id) === String(logJobData.agent_profile));
        const tier = agentObj?.price_tiers?.find((t) => t.service_type === newType);
        if (tier) {
          newAmount = tier.agreed_price;
        } else if (parseFloat(matchedService.default_price || "0") > 0) {
          newAmount = matchedService.default_price;
        }
      } else if (logJobData.referral_source !== "DIRECT") {
        if (parseFloat(matchedService.default_price || "0") > 0 && (logJobData.amount_paid === "0" || !logJobData.amount_paid)) {
          newAmount = matchedService.default_price;
        }
      }
    }

    setLogJobData(prev => ({
      ...prev,
      job_type: newType,
      job_cost: newCost,
      amount_paid: newAmount,
    }));
  };

  const handleLogJobReferralChange = (newSource: "WALK_IN" | "STAFF" | "DIRECT" | "AGENT") => {
    let newAmount = logJobData.amount_paid;
    let newAgentId = logJobData.agent_profile;
    const matchedService = serviceRates.find((s) => s.code === logJobData.job_type);

    if (newSource === "DIRECT") {
      newAmount = "0";
      newAgentId = "";
    } else if (newSource === "AGENT") {
      if (agents.length > 0 && !newAgentId) {
        newAgentId = String(agents[0].id);
        const tier = agents[0].price_tiers?.find((t) => t.service_type === logJobData.job_type);
        newAmount = tier ? tier.agreed_price : (matchedService?.default_price || "0");
      }
    } else if (newSource === "STAFF") {
      newAgentId = "";
      if (logJobData.job_type === "NERD" && (parseFloat(newAmount) < 11000 || newAmount === "0")) {
        newAmount = "11000";
      } else if (matchedService && (newAmount === "0" || !newAmount)) {
        newAmount = matchedService.default_price || "0";
      }
    } else { // WALK_IN
      newAgentId = "";
      if (matchedService && (newAmount === "0" || !newAmount)) {
        newAmount = matchedService.default_price || "0";
      }
    }

    setLogJobData(prev => ({
      ...prev,
      referral_source: newSource,
      agent_profile: newAgentId,
      amount_paid: newAmount,
    }));
  };

  const handleLogJobAgentChange = (agentId: string) => {
    let newAmount = logJobData.amount_paid;
    const agentObj = agents.find((a) => String(a.id) === String(agentId));
    if (agentObj) {
      const tier = agentObj.price_tiers?.find((t) => t.service_type === logJobData.job_type);
      if (tier) {
        newAmount = tier.agreed_price;
      }
    }
    setLogJobData(prev => ({
      ...prev,
      agent_profile: agentId,
      amount_paid: newAmount,
    }));
  };

  const logJobFinancialPreview = useMemo(() => {
    const paid = parseFloat(logJobData.amount_paid) || 0;
    const cost = parseFloat(logJobData.job_cost) || 0;
    let comm = 0;

    const matchedService = serviceRates.find((s) => s.code === logJobData.job_type);
    if (matchedService) {
      comm += parseFloat(matchedService.base_commission || "0");
    } else if (logJobData.job_type === "NERD" || logJobData.job_type === "AFFIDAVIT") {
      comm += 500;
    }

    if (logJobData.referral_source === "STAFF" && logJobData.job_type === "NERD") {
      if (paid < 11000) {
        return {
          valid: false,
          error: "Staff referrals on NERD require Amount Paid ≥ ₦11,000.",
          commission: 0,
          profit: 0,
        };
      }
      comm += 500; // Extra referral bonus
      if (paid > 11000) {
        comm += paid - 11000;
      }
    }

    const profit = paid - cost - comm;
    return {
      valid: true,
      error: null,
      commission: comm,
      profit: profit,
    };
  }, [logJobData.job_type, logJobData.referral_source, logJobData.amount_paid, logJobData.job_cost, serviceRates]);

  const handleLogJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLogJobError(null);

    if (!logJobData.customer_name.trim()) {
      setLogJobError("Customer name is required.");
      return;
    }

    if (logJobData.referral_source === "AGENT" && !logJobData.agent_profile) {
      setLogJobError("Please select an agent profile.");
      return;
    }

    const paidNum = parseFloat(logJobData.amount_paid) || 0;
    if (
      logJobData.referral_source === "STAFF" &&
      logJobData.job_type === "NERD" &&
      paidNum < 11000
    ) {
      setLogJobError("NERD Staff Referrals must have Amount Paid of at least ₦11,000.");
      return;
    }

    try {
      setLogJobSubmitting(true);
      const payload: any = {
        date: logJobData.date,
        customer_name: logJobData.customer_name.trim(),
        job_type: logJobData.job_type,
        referral_source: logJobData.referral_source,
        is_staff_referral: logJobData.referral_source === "STAFF",
        agent_profile: logJobData.referral_source === "AGENT" && logJobData.agent_profile ? parseInt(logJobData.agent_profile) : null,
        assigned_staff: logJobData.assigned_staff ? parseInt(logJobData.assigned_staff) : (user?.id || null),
        amount_paid: logJobData.referral_source === "DIRECT" ? "0.00" : (logJobData.amount_paid || "0.00"),
        payment_status: logJobData.payment_status,
        job_cost: logJobData.job_cost || "0.00",
        status: logJobData.status,
        is_approved: logJobData.is_approved,
        is_commission_paid: logJobData.is_commission_paid,
        remark: logJobData.remark.trim() || undefined,
      };

      await createJob(payload);
      setPromptMessage({
        title: "Job Logged Successfully",
        message: `Job for "${payload.customer_name}" has been logged with Full SuperAdmin authorization.`,
        type: "success",
      });
      setIsLogJobModalOpen(false);
      await loadData();
      if (activeTab === "payouts") {
        await loadPayoutSummary(selectedPayoutMonth);
      }
    } catch (err: any) {
      console.error("Failed to log job:", err);
      const errMsg = err.response?.data?.detail ||
        (err.response?.data && typeof err.response.data === "object" ? Object.values(err.response.data).flat().join(" ") : null) ||
        "Failed to log job. Please verify your inputs.";
      setLogJobError(String(errMsg));
    } finally {
      setLogJobSubmitting(false);
    }
  };

  const formatMonth = (m: string) => {
    if (!m || m === "ALL") return "All Months";
    try {
      const [year, month] = m.split("-");
      const d = new Date(parseInt(year), parseInt(month) - 1, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch {
      return m;
    }
  };

  // 1-Click Quick Approval Toggle
  const handleToggleApproval = async (job: Job) => {
    const nextApproved = !job.is_approved;
    try {
      // Optimistic update
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, is_approved: nextApproved } : j));
      await toggleCommissionApproval(job.id, nextApproved);
      await loadData();
      if (activeTab === "payouts") {
        await loadPayoutSummary(selectedPayoutMonth);
      }
    } catch (err) {
      console.error("Failed to toggle approval:", err);
      alert("Failed to update approval status.");
      await loadData();
    }
  };

  // 1-Click Quick Monthly Payout Toggle
  const handleToggleCommissionPaid = async (job: Job) => {
    if (!job.is_approved && !job.is_commission_paid) {
      alert("Commission must be approved first before it can be marked as disbursed.");
      return;
    }
    const nextPaid = !job.is_commission_paid;
    try {
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, is_commission_paid: nextPaid } : j));
      await toggleCommissionPaid(job.id, nextPaid);
      await loadData();
      if (activeTab === "payouts") {
        await loadPayoutSummary(selectedPayoutMonth);
      }
    } catch (err) {
      console.error("Failed to toggle commission paid status:", err);
      alert("Failed to update commission payment status.");
      await loadData();
    }
  };

  // Disburse Monthly Payout for a Staff Member
  const handleDisburseStaffSubmit = async () => {
    if (!disburseModalStaff) return;
    setDisbursing(true);
    try {
      const res = await disburseMonthlyCommission({
        staff_id: disburseModalStaff.staff_id,
        month: selectedPayoutMonth,
      });
      alert(res.detail || "Successfully disbursed monthly commission.");
      setDisburseModalStaff(null);
      await Promise.all([loadData(), loadPayoutSummary(selectedPayoutMonth)]);
    } catch (err: any) {
      console.error("Failed to disburse monthly commission:", err);
      alert(err.response?.data?.detail || "Failed to disburse monthly commission.");
    } finally {
      setDisbursing(false);
    }
  };

  // 1-Click Quick Status Update
  const handleStatusChange = async (job: Job, newStatus: JobStatus) => {
    try {
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, status: newStatus } : j))
      );
      await updateJob(job.id, { status: newStatus });
      await loadData();
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update job status.");
      await loadData();
    }
  };

  // Open Edit Financials Modal
  const handleOpenFinancialsModal = (job: Job) => {
    setSelectedJobForEdit(job);
    let initSource = job.referral_source;
    if (!initSource || (initSource as any) === "AGENT") {
      if (job.is_staff_referral) initSource = "STAFF";
      else initSource = "DIRECT";
    }
    setEditReferralSource(initSource);
    setEditStatus(job.status || "PENDING");
    setEditPaymentStatus(job.payment_status || "PAID");
    const agentIdStr = job.agent_profile ? String(job.agent_profile) : "";
    setEditAgentProfileId(agentIdStr);

    let initialAmount = job.amount_paid ? String(job.amount_paid) : "0.00";
    if (agentIdStr && parseFloat(initialAmount) === 0) {
      const agentObj = agents.find((a) => String(a.id) === agentIdStr);
      const tier = agentObj?.price_tiers?.find((t) => t.service_type === job.job_type);
      if (tier && tier.agreed_price) {
        initialAmount = String(tier.agreed_price);
      }
    }
    setEditAmountPaid(initialAmount);

    let initialCost = job.job_cost ? String(job.job_cost) : "0.00";
    if (!initialCost || parseFloat(initialCost) === 0) {
      const sr = serviceRates.find((s) => s.code === job.job_type);
      if (sr && sr.default_cost && parseFloat(String(sr.default_cost)) > 0) {
        initialCost = String(sr.default_cost);
      }
    }
    setEditJobCost(initialCost);

    let initialComm = job.commission ? String(job.commission) : "";
    if (!initialComm || parseFloat(initialComm) === 0) {
      initialComm = job.expected_commission ? String(job.expected_commission) : "0.00";
    }
    setEditCommission(initialComm);

    setEditIsApproved(job.is_approved);
    setEditIsCommissionPaid(job.is_commission_paid);
  };

  const handleAgentChange = (agentId: string) => {
    setEditAgentProfileId(agentId);
    if (!agentId || !selectedJobForEdit) return;

    const selectedAgent = agents.find((a) => String(a.id) === String(agentId));
    if (selectedAgent) {
      const tier = selectedAgent.price_tiers?.find(
        (t) => t.service_type === selectedJobForEdit.job_type
      );
      if (tier && tier.agreed_price) {
        setEditAmountPaid(String(tier.agreed_price));
      }
    }
  };

  const handleSaveFinancials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedJobForEdit) return;

    setCostSubmitting(true);
    try {
      const agentProfileVal = editAgentProfileId ? Number(editAgentProfileId) : null;
      await updateJob(selectedJobForEdit.id, {
        referral_source: editReferralSource as any,
        agent_profile: agentProfileVal,
        is_staff_referral: editReferralSource === "STAFF",
        amount_paid: editAmountPaid || "0.00",
        payment_status: editPaymentStatus,
        job_cost: editJobCost || "0.00",
        commission: editCommission !== "" ? editCommission : "0.00",
        status: editStatus,
        is_approved: editIsApproved,
        is_commission_paid: editIsCommissionPaid,
      });
      setSelectedJobForEdit(null);
      await Promise.all([loadData(), activeTab === "payouts" ? loadPayoutSummary(selectedPayoutMonth) : Promise.resolve()]);
    } catch (err: any) {
      console.error("Failed to save financials:", err);
      const msg = err.response?.data?.agent_profile?.[0] || err.response?.data?.detail || "Failed to update job financials.";
      alert(msg);
    } finally {
      setCostSubmitting(false);
    }
  };

  // 1-Click Quick Payment Status Update
  const handlePaymentStatusChange = async (job: Job, newPaymentStatus: PaymentStatus) => {
    try {
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, payment_status: newPaymentStatus } : j))
      );
      await updateJob(job.id, { payment_status: newPaymentStatus });
      await loadData();
    } catch (err) {
      console.error("Failed to update payment status:", err);
      alert("Failed to update payment status.");
      await loadData();
    }
  };

  // Create New Wholesale Agent
  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAgentName.trim()) return;

    setAgentSubmitting(true);
    try {
      const created = await createAgent({
        name: newAgentName.trim(),
        phone_number: newAgentPhone.trim() || undefined,
        email: newAgentEmail.trim() || undefined,
      });
      setIsAgentModalOpen(false);
      setNewAgentName("");
      setNewAgentPhone("");
      setNewAgentEmail("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      if (created && created.id && selectedJobForEdit) {
        setEditAgentProfileId(String(created.id));
        handleAgentChange(String(created.id));
      }
    } catch (err) {
      console.error("Failed to create agent:", err);
      alert("Failed to register agent.");
    } finally {
      setAgentSubmitting(false);
    }
  };

  // Delete Prompt Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);
  const [promptMessage, setPromptMessage] = useState<{ title: string; message: string; type: "error" | "info" | "success" } | null>(null);

  // Prompt Delete Job
  const handlePromptDeleteJob = (job: Job) => {
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteJob = async () => {
    if (!jobToDelete) return;
    try {
      setDeleteSubmitting(true);
      await deleteJob(jobToDelete.id);
      setDeleteModalOpen(false);
      setJobToDelete(null);
      await loadData();
    } catch (err) {
      console.error("Failed to delete job:", err);
      setPromptMessage({
        title: "Delete Failed",
        message: "Failed to delete job record. Please check your network and permissions.",
        type: "error",
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Service Rate Management Handlers ─────────────────────────────────────
  const handleOpenNewServiceModal = () => {
    setSelectedServiceForEdit(null);
    setServiceFormName("");
    setServiceFormCode("");
    setServiceFormBaseCommission("0");
    setServiceFormDefaultPrice("0");
    setServiceFormDefaultCost("0");
    setServiceFormOrder(String(serviceRates.length + 1));
    setServiceFormIsActive(true);
    setIsServiceModalOpen(true);
  };

  const handleOpenEditServiceModal = (sr: ServiceRate) => {
    setSelectedServiceForEdit(sr);
    setServiceFormName(sr.name);
    setServiceFormCode(sr.code);
    setServiceFormBaseCommission(sr.base_commission || "0");
    setServiceFormDefaultPrice(sr.default_price || "0");
    setServiceFormDefaultCost(sr.default_cost || "0");
    setServiceFormOrder(String(sr.order || 0));
    setServiceFormIsActive(sr.is_active);
    setIsServiceModalOpen(true);
  };

  const handleSaveService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceFormName.trim() || !serviceFormCode.trim()) {
      setPromptMessage({
        title: "Validation Error",
        message: "Service Name and Service Code are required.",
        type: "error",
      });
      return;
    }

    try {
      setServiceSubmitting(true);
      const payload: Partial<ServiceRate> = {
        name: serviceFormName.trim(),
        code: serviceFormCode.trim().toUpperCase(),
        base_commission: serviceFormBaseCommission.trim() || "0",
        default_price: serviceFormDefaultPrice.trim() || "0",
        default_cost: serviceFormDefaultCost.trim() || "0",
        order: parseInt(serviceFormOrder) || 0,
        is_active: serviceFormIsActive,
      };

      if (selectedServiceForEdit) {
        await updateServiceRate(selectedServiceForEdit.id, payload);
        setPromptMessage({
          title: "Service Updated",
          message: `Service "${payload.name}" updated successfully.`,
          type: "success",
        });
      } else {
        await createServiceRate(payload);
        setPromptMessage({
          title: "Service Created",
          message: `New service "${payload.name}" created successfully.`,
          type: "success",
        });
      }

      setIsServiceModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceRates.all });
    } catch (err: any) {
      console.error("Failed to save service rate", err);
      const msg = err.response?.data?.code?.[0] || err.response?.data?.detail || "Failed to save service. Please ensure the code is unique.";
      setPromptMessage({
        title: "Save Failed",
        message: String(msg),
        type: "error",
      });
    } finally {
      setServiceSubmitting(false);
    }
  };

  const handleToggleServiceActive = async (sr: ServiceRate) => {
    try {
      await updateServiceRate(sr.id, { is_active: !sr.is_active });
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceRates.all });
    } catch (err: any) {
      console.error("Failed to toggle service status", err);
      setPromptMessage({
        title: "Error",
        message: "Failed to update service active status.",
        type: "error",
      });
    }
  };

  const handleConfirmDeleteService = async () => {
    if (!serviceToDelete) return;
    try {
      setServiceDeleteSubmitting(true);
      await deleteServiceRate(serviceToDelete.id);
      setServiceToDelete(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceRates.all });
      setPromptMessage({
        title: "Service Deleted",
        message: `Service "${serviceToDelete.name}" has been removed.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Failed to delete service", err);
      setPromptMessage({
        title: "Delete Failed",
        message: "Could not delete service record. It may be referenced in existing jobs.",
        type: "error",
      });
    } finally {
      setServiceDeleteSubmitting(false);
    }
  };

  // Overall Financial KPIs (Filtered by active month filter if selected)
  const financials = useMemo(() => {
    if (serverAnalytics) {
      const a = serverAnalytics as any;
      return {
        totalJobs: Number(a.total_jobs ?? a.totalJobs ?? 0) || 0,
        pendingJobs: Number(a.pending_jobs ?? a.pendingJobs ?? 0) || 0,
        grossRevenue: Number(a.gross_revenue ?? a.grossRevenue ?? 0) || 0,
        totalCosts: Number(a.total_costs ?? a.totalCosts ?? 0) || 0,
        totalCommissions: Number(a.total_commissions ?? a.totalCommissions ?? 0) || 0,
        approvedCommissions: Number(a.approved_commissions ?? a.approved_commission ?? a.approvedCommissions ?? 0) || 0,
        pendingCommissions: Number(a.pending_commissions ?? a.awaiting_approval_commission ?? a.pendingCommissions ?? 0) || 0,
        paidCommissions: Number(a.paid_commissions ?? a.paid_commission ?? a.paidCommissions ?? 0) || 0,
        pendingMonthlyPayout: Number(a.pending_monthly_payout ?? a.pendingMonthlyPayout ?? 0) || 0,
        netProfit: Number(a.net_profit ?? a.netProfit ?? 0) || 0,
      };
    }

    let grossRevenue = 0;
    let totalCosts = 0;
    let totalCommissions = 0;
    let approvedCommissions = 0;
    let pendingCommissions = 0;
    let paidCommissions = 0;
    let pendingMonthlyPayout = 0;
    let netProfit = 0;

    const safeJobs = Array.isArray(jobs) ? jobs : [];
    const targetJobs =
      monthFilter === "ALL"
        ? safeJobs
        : safeJobs.filter((j) => j?.date && j.date.startsWith(monthFilter));

    const totalJobs = targetJobs.length;
    const pendingJobs = targetJobs.filter(
      (j) => j && (j.status === "PENDING" || j.status === "IN_PROGRESS")
    ).length;

    targetJobs.forEach(j => {
      if (!j) return;
      const paid = parseFloat(j.amount_paid || "0") || 0;
      const cost = parseFloat(j.job_cost || "0") || 0;
      const comm = parseFloat(j.commission || "0") || 0;
      const prof = parseFloat(j.profit || "0") || (paid - cost - comm);

      grossRevenue += paid;
      totalCosts += cost;
      totalCommissions += comm;
      netProfit += prof;

      if (j.is_approved) {
        approvedCommissions += comm;
        if (j.is_commission_paid) {
          paidCommissions += comm;
        } else {
          pendingMonthlyPayout += comm;
        }
      } else {
        pendingCommissions += comm;
      }
    });

    return {
      totalJobs,
      pendingJobs,
      grossRevenue,
      totalCosts,
      totalCommissions,
      approvedCommissions,
      pendingCommissions,
      paidCommissions,
      pendingMonthlyPayout,
      netProfit,
    };
  }, [jobs, monthFilter, serverAnalytics]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col cht-gap-1">
        <div className="cht-flex cht-items-center cht-gap-2">
          <h1 className="cht-text-2xl sm:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
            Job & Commission Audit Console
          </h1>
          <span className="cht-px-2.5 cht-py-0.5 cht-bg-amber-100 cht-text-amber-800 cht-rounded-full cht-text-xs cht-font-black">
            SuperAdmin Master
          </span>
        </div>
        <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
          Organization-wide revenue oversight, job cost calibration, and worker commission authorizations.
        </p>
      </header>

      {/* Navigation Tabs & Actions Bar */}
      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-3 cht-w-full">
        {/* Horizontal scroll container on mobile with hidden scrollbar */}
        <div className="cht-w-full sm:cht-w-auto cht-overflow-x-auto cht-scrollbar-hide [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden -cht-mx-4 cht-px-4 sm:cht-mx-0 sm:cht-px-0">
          <div className="cht-bg-gray-100 cht-p-1 cht-rounded-2xl cht-inline-flex cht-items-center cht-gap-1 cht-w-max">
            <button
              onClick={() => setActiveTab("jobs")}
              className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all cht-whitespace-nowrap cht-shrink-0 ${
                activeTab === "jobs"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                  : "cht-text-gray-500 hover:cht-text-gray-900"
              }`}
            >
              All Staff Jobs ({totalCount || jobs.length})
            </button>
            <button
              onClick={() => setActiveTab("payouts")}
              className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all cht-flex cht-items-center cht-gap-1.5 cht-whitespace-nowrap cht-shrink-0 ${
                activeTab === "payouts"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                  : "cht-text-gray-500 hover:cht-text-gray-900"
              }`}
            >
              <Wallet size={14} />
              Monthly Payouts
              {financials.pendingMonthlyPayout > 0 && (
                <span className="cht-w-2 cht-h-2 cht-rounded-full cht-bg-indigo-500 cht-inline-block" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("agents")}
              className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all cht-whitespace-nowrap cht-shrink-0 ${
                activeTab === "agents"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                  : "cht-text-gray-500 hover:cht-text-gray-900"
              }`}
            >
              Wholesale Agents ({agents.length})
            </button>
            <button
              onClick={() => setActiveTab("services")}
              className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all cht-flex cht-items-center cht-gap-1.5 cht-whitespace-nowrap cht-shrink-0 ${
                activeTab === "services"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                  : "cht-text-gray-500 hover:cht-text-gray-900"
              }`}
            >
              <SlidersHorizontal size={14} />
              Services & Rates ({serviceRates.length})
            </button>
          </div>
        </div>

        {/* Action Controls */}
        <div className="cht-flex cht-items-center cht-gap-2 cht-shrink-0 cht-self-start sm:cht-self-auto">
          <Button
            variant="outline"
            leftIcon={<RefreshCw size={16} className={refreshing ? "cht-animate-spin" : ""} />}
            onClick={handleRefresh}
            className="cht-rounded-2xl"
          >
            Refresh
          </Button>

          {activeTab === "jobs" && (
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={handleOpenLogJobModal}
              className="cht-rounded-2xl cht-shadow-md hover:cht-shadow-lg"
            >
              Log Job
            </Button>
          )}

          {activeTab === "agents" && (
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={() => setIsAgentModalOpen(true)}
              className="cht-rounded-2xl"
            >
              Add Agent
            </Button>
          )}

          {activeTab === "services" && (
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={handleOpenNewServiceModal}
              className="cht-rounded-2xl"
            >
              Add Service
            </Button>
          )}
        </div>
      </div>

      {/* SuperAdmin Master KPIs */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 xl:cht-grid-cols-5 cht-gap-4 lg:cht-gap-6">
        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl cht-shrink-0">
            <Clock size={24} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-amber-600 cht-uppercase cht-tracking-widest">
              Total Pending Jobs {monthFilter !== "ALL" ? `(${formatMonth(monthFilter)})` : ""}
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-amber-700">
              {financials.pendingJobs}
            </h4>
            <div className="cht-text-[10px] cht-text-gray-400 cht-font-bold cht-mt-0.5">
              of {financials.totalJobs} total jobs
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("PENDING_JOBS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Pending Jobs"
            aria-label="Pending Jobs information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl cht-shrink-0">
            <DollarSign size={24} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Gross Revenue {monthFilter !== "ALL" ? `(${formatMonth(monthFilter)})` : ""}
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{(financials.grossRevenue || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("GROSS_REVENUE");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-blue-600 hover:cht-bg-blue-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Gross Revenue"
            aria-label="Gross Revenue information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-rose-50 cht-text-rose-600 cht-rounded-2xl cht-shrink-0">
            <Percent size={24} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-rose-500 cht-uppercase cht-tracking-widest">Total Job Costs</p>
            <h4 className="cht-text-2xl cht-font-black cht-text-rose-700">
              ₦{(financials.totalCosts || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("TOTAL_COSTS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Total Job Costs"
            aria-label="Total Job Costs information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl cht-shrink-0">
            <ShieldCheck size={24} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-amber-600 cht-uppercase cht-tracking-widest">
              Commissions {monthFilter !== "ALL" ? `(${formatMonth(monthFilter)})` : ""}
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-amber-700">
              ₦{(financials.totalCommissions || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </h4>
            <div className="cht-text-[10px] cht-text-gray-500 cht-font-bold cht-mt-0.5">
              Paid: <span className="cht-text-emerald-700">₦{(financials.paidCommissions || 0).toLocaleString('en-NG')}</span> | Pend: <span className="cht-text-indigo-600">₦{(financials.pendingMonthlyPayout || 0).toLocaleString('en-NG')}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("COMMISSIONS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Commissions"
            aria-label="Commissions information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl cht-shrink-0">
            <TrendingUp size={24} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-emerald-600 cht-uppercase cht-tracking-widest">Net Platform Profit</p>
            <h4 className="cht-text-2xl cht-font-black cht-text-emerald-700">
              ₦{(financials.netProfit || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("NET_PROFIT");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Net Platform Profit"
            aria-label="Net Platform Profit information"
          >
            <Info size={15} />
          </button>
        </GlassCard>
      </div>

      {/* View: All Staff Jobs */}
      {activeTab === "jobs" && (
        <div className="cht-space-y-6">
          {/* Search & Filter Toolbar */}
          <div className="cht-space-y-3 cht-bg-white cht-p-4 sm:cht-p-5 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
            {/* Row 1: Dedicated Search Bar */}
            <div className="cht-w-full">
              <Input
                leftIcon={<Search size={18} />}
                placeholder="Search by customer name, staff member, agent, SN, or job ID..."
                value={searchQuery}
                onChange={(e: any) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                rightElement={
                  searchQuery ? (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="cht-text-gray-400 hover:cht-text-gray-600 cht-transition-colors cht-p-1"
                      title="Clear search"
                    >
                      <X size={16} />
                    </button>
                  ) : null
                }
              />
            </div>

            {/* Row 2: Filter Controls */}
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
              {/* Filter by Staff Member */}
              <div className="cht-w-48">
                <Input
                  as="select"
                  value={staffFilter}
                  onChange={(e: any) => {
                    setStaffFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Staff Members", value: "ALL" },
                    { label: "Unassigned Jobs", value: "UNASSIGNED" },
                    ...staffMembers.map((s) => ({
                      label: s.name,
                      value: s.id,
                    })),
                  ]}
                />
              </div>

              <div className="cht-w-44">
                <Input
                  as="select"
                  value={monthFilter}
                  onChange={(e: any) => {
                    const newM = e.target.value;
                    setMonthFilter(newM);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Months", value: "ALL" },
                    ...availableMonths.map((m) => ({
                      label: `${formatMonth(m)}${m === currentMonthStr ? " (Current)" : ""}`,
                      value: m,
                    })),
                  ]}
                />
              </div>

              <div className="cht-w-44">
                <Input
                  as="select"
                  value={referralFilter}
                  onChange={(e: any) => {
                    setReferralFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Referral Sources", value: "ALL" },
                    { label: "Direct Channel", value: "DIRECT" },
                    { label: "Walk-in", value: "WALK_IN" },
                    { label: "Staff Referral", value: "STAFF" },
                  ]}
                />
              </div>

              <div className="cht-w-48">
                <Input
                  as="select"
                  value={agentFilter}
                  onChange={(e: any) => {
                    setAgentFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Agent", value: "ALL" },
                    ...agents.map((a) => ({
                      label: a.name,
                      value: String(a.id),
                    })),
                  ]}
                />
              </div>

              <div className="cht-w-40">
                <Input
                  as="select"
                  value={payoutFilter}
                  onChange={(e: any) => {
                    setPayoutFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Payouts", value: "ALL" },
                    { label: "Paid Out Only", value: "PAID" },
                    { label: "Pending Payout", value: "UNPAID" },
                  ]}
                />
              </div>

              <div className="cht-w-40">
                <Input
                  as="select"
                  value={paymentStatusFilter}
                  onChange={(e: any) => {
                    setPaymentStatusFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Client Pay", value: "ALL" },
                    { label: "Paid", value: "PAID" },
                    { label: "Unpaid", value: "UNPAID" },
                    { label: "Partial", value: "PARTIAL" },
                  ]}
                />
              </div>

              <div className="cht-w-40">
                <Input
                  as="select"
                  value={approvalFilter}
                  onChange={(e: any) => {
                    setApprovalFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Approvals", value: "ALL" },
                    { label: "Approved Only", value: "APPROVED" },
                    { label: "Pending Approval Only", value: "PENDING" },
                  ]}
                />
              </div>

              <div className="cht-w-36">
                <Input
                  as="select"
                  value={statusFilter}
                  onChange={(e: any) => {
                    setStatusFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Statuses", value: "ALL" },
                    { label: "Pending", value: "PENDING" },
                    { label: "In Progress", value: "IN_PROGRESS" },
                    { label: "Completed", value: "COMPLETED" },
                    { label: "Cancelled", value: "CANCELLED" },
                  ]}
                />
              </div>

              <div className="cht-w-44">
                <Input
                  as="select"
                  value={typeFilter}
                  onChange={(e: any) => {
                    setTypeFilter(e.target.value);
                    if (currentPage !== 1) updateQueryParams({ page: 1 });
                  }}
                  options={[
                    { label: "All Service Types", value: "ALL" },
                    ...(dynamicServiceTypes || []).map((st) => ({
                      label: st.label,
                      value: st.value,
                    })),
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Master Audit Table */}
          <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
            {loading ? (
              <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3 cht-text-gray-400">
                <RefreshCw size={32} className="cht-animate-spin cht-text-[#3e57da]" />
                <p className="cht-font-medium cht-text-sm">Loading master audit records...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="cht-py-20 cht-text-center cht-space-y-4">
                <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-text-gray-400 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto">
                  <Briefcase size={28} />
                </div>
                <div>
                  <h3 className="cht-text-lg cht-font-black cht-text-gray-800">No Job Records Match Filter</h3>
                  <p className="cht-text-gray-400 cht-text-sm cht-mt-1">Try resetting search parameters or filters.</p>
                </div>
                <div>
                  <Button
                    variant="primary"
                    leftIcon={<Plus size={18} />}
                    onClick={handleOpenLogJobModal}
                    className="cht-rounded-2xl"
                  >
                    Log New Job
                  </Button>
                </div>
              </div>
            ) : (
              <div className="cht-overflow-x-auto cht-relative">
                {tableLoading && (
                  <div className="cht-absolute cht-inset-0 cht-bg-white/60 cht-backdrop-blur-[1px] cht-flex cht-items-center cht-justify-center cht-z-10 cht-transition-opacity">
                    <RefreshCw size={24} className="cht-animate-spin cht-text-[#3e57da]" />
                  </div>
                )}
                <table className="cht-w-full cht-text-left cht-border-collapse">
                  <thead>
                    <tr className="cht-border-b cht-border-gray-100/80 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      <th className="cht-py-4 cht-px-6">SN / Date</th>
                      <th className="cht-py-4 cht-px-6">Staff Member</th>
                      <th className="cht-py-4 cht-px-6">Customer / Job</th>
                      <th className="cht-py-4 cht-px-6">Status</th>
                      <th className="cht-py-4 cht-px-6">Paid</th>
                      <th className="cht-py-4 cht-px-6">Job Cost</th>
                      <th className="cht-py-4 cht-px-6">Commission</th>
                      <th className="cht-py-4 cht-px-6">Net Profit</th>
                      <th className="cht-py-4 cht-px-6">Approval</th>
                      <th className="cht-py-4 cht-px-6">Monthly Payout</th>
                      <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="cht-divide-y cht-divide-gray-50">
                    {jobs.map((job, idx) => {
                      const seqNum = (currentPage - 1) * pageSize + idx + 1;
                      const paid = parseFloat(job.amount_paid || "0") || 0;
                      const baseRate = serviceRates.find((sr) => sr.code === job.job_type);
                      const defaultCost = baseRate && baseRate.default_cost ? parseFloat(String(baseRate.default_cost)) : 0;
                      const cost = parseFloat(job.job_cost || "0") || defaultCost;
                      const comm = parseFloat(job.commission || "0") || (parseFloat(job.expected_commission || "0") || 0);
                      const prof = parseFloat(job.profit || "0") || (paid - cost - comm);
                      const defaultComm = baseRate && baseRate.base_commission ? parseFloat(baseRate.base_commission) : (job.job_type === "NERD" || job.job_type === "AFFIDAVIT" ? 500 : (parseFloat(job.expected_commission || "0") || 0));
                      const isCustomComm = defaultComm > 0 && Math.abs(comm - defaultComm) > 0.01;

                      return (
                        <tr key={job.id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-font-black cht-text-sm cht-text-gray-900">#{seqNum}</div>
                            <div className="cht-text-xs cht-text-gray-400 cht-font-medium">{job.date || "Today"}</div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-font-bold cht-text-sm cht-text-gray-800">
                              {job.assigned_staff_name || "Unassigned"}
                            </div>
                            <div className="cht-text-[11px] cht-text-gray-400">ID: {job.assigned_staff || "—"}</div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-font-bold cht-text-sm cht-text-gray-900">{job.customer_name}</div>
                            {job.remark && (
                              <div
                                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-mt-1.5 cht-mb-1 cht-px-2.5 cht-py-1 cht-bg-rose-50 cht-border cht-border-rose-200/90 cht-rounded-lg cht-text-rose-700 cht-text-[11px] cht-font-bold cht-max-w-xs cht-truncate"
                                title={job.remark}
                              >
                                <span className="cht-w-1.5 cht-h-1.5 cht-rounded-full cht-bg-rose-500 cht-flex-shrink-0" />
                                <span className="cht-truncate">{job.remark}</span>
                              </div>
                            )}
                            <div className="cht-mt-1 cht-space-y-1">
                              <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-1.5">
                                <span className="cht-inline-block cht-px-2 cht-py-0.5 cht-bg-slate-100 cht-text-slate-800 cht-font-bold cht-text-[10px] cht-rounded">
                                  {job.job_type_name || job.job_type}
                                </span>
                                {job.is_split && (
                                  <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-100 cht-rounded cht-text-[10px] cht-font-black">
                                    <Users size={10} />
                                    Split ({job.assignments?.length || 2} specialists)
                                  </span>
                                )}
                              </div>
                              <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-1.5">
                                {job.referral_source === "DIRECT" || (!job.referral_source && !job.is_staff_referral) ? (
                                  <span className="cht-inline-block cht-px-2 cht-py-0.5 cht-bg-blue-100 cht-text-[#3e57da] cht-font-bold cht-text-[10px] cht-rounded">
                                    Direct {paid === 0 ? "(Needs Price)" : ""}
                                  </span>
                                ) : job.referral_source === "STAFF" || job.is_staff_referral ? (
                                  <span className="cht-inline-block cht-px-2 cht-py-0.5 cht-bg-purple-100 cht-text-purple-800 cht-font-bold cht-text-[10px] cht-rounded">
                                    Staff Referral
                                  </span>
                                ) : (
                                  <span className="cht-inline-block cht-px-2 cht-py-0.5 cht-bg-slate-100 cht-text-slate-700 cht-font-medium cht-text-[10px] cht-rounded">
                                    Walk-in
                                  </span>
                                )}
                                {job.agent_profile_name && (
                                  <span
                                    className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-cyan-100 cht-text-cyan-800 cht-font-bold cht-text-[10px] cht-rounded"
                                    title={`Agent: ${job.agent_profile_name}`}
                                  >
                                    <User size={11} className="cht-text-cyan-700 cht-flex-shrink-0" />
                                    <span>{job.agent_profile_name}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-relative cht-inline-block">
                              <select
                                value={job.status || "PENDING"}
                                onChange={(e) => handleStatusChange(job, e.target.value as JobStatus)}
                                className={`cht-appearance-none cht-cursor-pointer cht-text-[11px] cht-font-black cht-px-3 cht-py-1 cht-pr-6 cht-rounded-full cht-border cht-transition-all focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 ${
                                  job.status === "COMPLETED"
                                    ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border-emerald-200 hover:cht-bg-emerald-100"
                                    : job.status === "IN_PROGRESS"
                                    ? "cht-bg-blue-50 cht-text-blue-700 cht-border-blue-200 hover:cht-bg-blue-100"
                                    : job.status === "CANCELLED"
                                    ? "cht-bg-rose-50 cht-text-rose-700 cht-border-rose-200 hover:cht-bg-rose-100"
                                    : "cht-bg-amber-50 cht-text-amber-700 cht-border-amber-200 hover:cht-bg-amber-100"
                                }`}
                                title="Click to change job execution status"
                              >
                                <option value="PENDING">Pending</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="CANCELLED">Cancelled</option>
                              </select>
                              <div className="cht-pointer-events-none cht-absolute cht-inset-y-0 cht-right-0 cht-flex cht-items-center cht-pr-2 cht-text-gray-400">
                                <svg className="cht-w-2.5 cht-h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-space-y-1.5">
                              <span className="cht-font-bold cht-text-sm cht-text-gray-900 cht-block">
                                ₦{paid.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                              </span>
                              <div className="cht-relative cht-inline-block">
                                <select
                                  value={job.payment_status || "PAID"}
                                  onChange={(e) => handlePaymentStatusChange(job, e.target.value as PaymentStatus)}
                                  className={`cht-appearance-none cht-cursor-pointer cht-text-[10px] cht-font-black cht-px-2 cht-py-0.5 cht-pr-5 cht-rounded-full cht-border cht-transition-all focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 ${
                                    (job.payment_status || "PAID") === "PAID"
                                      ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border-emerald-200 hover:cht-bg-emerald-100"
                                      : job.payment_status === "PARTIAL"
                                      ? "cht-bg-indigo-50 cht-text-indigo-700 cht-border-indigo-200 hover:cht-bg-indigo-100"
                                      : "cht-bg-rose-50 cht-text-rose-700 cht-border-rose-200 hover:cht-bg-rose-100"
                                  }`}
                                  title="Click to change client payment status"
                                >
                                  <option value="PAID">Paid</option>
                                  <option value="UNPAID">Unpaid</option>
                                  <option value="PARTIAL">Partial</option>
                                </select>
                                <div className="cht-pointer-events-none cht-absolute cht-inset-y-0 cht-right-0 cht-flex cht-items-center cht-pr-1.5 cht-text-gray-400">
                                  <svg className="cht-w-2 cht-h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-bold cht-text-sm cht-text-rose-600">
                              ₦{cost.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-flex cht-items-center cht-gap-1.5">
                              <span className="cht-font-black cht-text-sm cht-text-[#3e57da]">
                                ₦{comm.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleOpenFinancialsModal(job)}
                                className="cht-p-1 cht-text-gray-400 hover:cht-text-[#3e57da] cht-rounded-lg hover:cht-bg-blue-50 cht-transition-colors"
                                title="Calibrate commission for this job"
                              >
                                <Edit2 size={12} />
                              </button>
                            </div>
                            {isCustomComm && (
                              <span className="cht-text-[10px] cht-font-bold cht-text-amber-600 cht-block cht-mt-0.5">
                                Custom (DB: ₦{defaultComm.toLocaleString('en-NG')})
                              </span>
                            )}
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className={`cht-font-black cht-text-sm ${prof >= 0 ? "cht-text-emerald-600" : "cht-text-rose-600"}`}>
                              ₦{prof.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <button
                              onClick={() => handleToggleApproval(job)}
                              className={`cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-text-xs cht-font-black cht-transition-all ${
                                job.is_approved
                                  ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 hover:cht-bg-emerald-100"
                                  : "cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200 hover:cht-bg-amber-100"
                              }`}
                              title="Click to toggle commission authorization"
                            >
                              {job.is_approved ? (
                                <>
                                  <ShieldCheck size={14} className="cht-text-emerald-600" />
                                  <span>Authorized</span>
                                </>
                              ) : (
                                <>
                                  <Clock size={14} className="cht-text-amber-600" />
                                  <span>Approve Now</span>
                                </>
                              )}
                            </button>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            {job.is_commission_paid ? (
                              <button
                                onClick={() => handleToggleCommissionPaid(job)}
                                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 hover:cht-bg-emerald-100 cht-rounded-full cht-text-xs cht-font-black cht-transition-all"
                                title={job.commission_paid_at ? `Paid on ${new Date(job.commission_paid_at).toLocaleDateString()}. Click to revoke.` : "Paid Out"}
                              >
                                <CheckCircle2 size={13} className="cht-text-emerald-600" />
                                <span>Paid Out</span>
                              </button>
                            ) : job.is_approved ? (
                              <button
                                onClick={() => handleToggleCommissionPaid(job)}
                                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200 hover:cht-bg-indigo-100 cht-rounded-full cht-text-xs cht-font-bold cht-transition-all"
                                title="Approved. Click to mark as disbursed in monthly payroll."
                              >
                                <Clock size={13} className="cht-text-indigo-600" />
                                <span>Disburse</span>
                              </button>
                            ) : (
                              <span className="cht-text-xs cht-text-gray-400 cht-font-medium">
                                Unapproved
                              </span>
                            )}
                          </td>

                          <td className="cht-py-4 cht-px-6 cht-text-right">
                            <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                              <button
                                onClick={() => handleOpenHandoffModal(job)}
                                className="cht-p-2 cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da] cht-rounded-xl cht-transition-colors"
                                title="Handoff / Split Deliverable with another specialist"
                              >
                                <ArrowRightLeft size={15} />
                              </button>

                              <button
                                onClick={() => handleOpenFinancialsModal(job)}
                                className="cht-p-2 cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-blue-50 hover:cht-text-[#3e57da] cht-rounded-xl cht-transition-colors"
                                title="Edit Job Cost & Financials"
                              >
                                <SlidersHorizontal size={15} />
                              </button>

                              <button
                                onClick={() => handlePromptDeleteJob(job)}
                                className="cht-p-2 cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-rose-50 hover:cht-text-rose-600 cht-rounded-xl cht-transition-colors"
                                title="Delete Job Record"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Footer */}
            {totalCount > 0 && (
              <PaginationFooter
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={tableLoading}
              />
            )}
          </GlassCard>
        </div>
      )}

      {/* View: Monthly Commission Payroll & Payouts Tab */}
      {activeTab === "payouts" && (
        <div className="cht-space-y-6">
          {/* Header Controls for Monthly Payroll */}
          <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-stretch md:cht-items-center cht-justify-between cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-p-3 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl">
                <Calendar size={20} />
              </div>
              <div>
                <h3 className="cht-text-sm cht-font-black cht-text-gray-900">
                  Monthly Commission Payroll Roster
                </h3>
                <p className="cht-text-xs cht-text-gray-400">
                  Reconcile and disburse accrued worker commissions on a calendar month cycle.
                </p>
              </div>
            </div>

            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-56">
                <Input
                  as="select"
                  value={selectedPayoutMonth}
                  onChange={(e: any) => setSelectedPayoutMonth(e.target.value)}
                  options={availableMonths.map((m) => ({
                    label: `${formatMonth(m)}${m === currentMonthStr ? " (Current Month)" : ""}`,
                    value: m,
                  }))}
                />
              </div>

              <Button
                variant="outline"
                leftIcon={<RefreshCw size={15} className={loadingPayoutSummary ? "cht-animate-spin" : ""} />}
                onClick={() => loadPayoutSummary(selectedPayoutMonth)}
                className="cht-rounded-2xl"
              >
                Reload
              </Button>
            </div>
          </div>

          {/* Month Summary Cards */}
          {payoutSummary?.summary && (
            <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 xl:cht-grid-cols-5 cht-gap-4 lg:cht-gap-6">
              <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
                <div className="cht-p-3.5 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-amber-600 cht-uppercase cht-tracking-widest">
                    Pending Jobs ({formatMonth(selectedPayoutMonth)})
                  </p>
                  <h4 className="cht-text-xl cht-font-black cht-text-amber-700">
                    {payoutSummary.summary.pending_jobs_count ?? 0}
                  </h4>
                  <div className="cht-text-[10px] cht-text-gray-400 cht-font-bold cht-mt-0.5">
                    of {payoutSummary.summary.total_jobs} logged
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
                <div className="cht-p-3.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-2xl">
                  <DollarSign size={20} />
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Approved Commission
                  </p>
                  <h4 className="cht-text-xl cht-font-black cht-text-gray-900">
                    ₦{parseFloat(payoutSummary.summary.total_earned || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </h4>
                </div>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
                <div className="cht-p-3.5 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-emerald-600 cht-uppercase cht-tracking-widest">
                    Disbursed to Date
                  </p>
                  <h4 className="cht-text-xl cht-font-black cht-text-emerald-700">
                    ₦{parseFloat(payoutSummary.summary.total_paid || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </h4>
                </div>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
                <div className="cht-p-3.5 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl">
                  <Wallet size={20} />
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-indigo-600 cht-uppercase cht-tracking-widest">
                    Pending Monthly Payout
                  </p>
                  <h4 className="cht-text-xl cht-font-black cht-text-indigo-700">
                    ₦{parseFloat(payoutSummary.summary.total_pending_payout || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </h4>
                </div>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
                <div className="cht-p-3.5 cht-bg-slate-100 cht-text-slate-700 cht-rounded-2xl">
                  <User size={20} />
                </div>
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Staff on Roster
                  </p>
                  <h4 className="cht-text-xl cht-font-black cht-text-gray-900">
                    {payoutSummary.summary.active_staff_count} Workers
                  </h4>
                </div>
              </GlassCard>
            </div>
          )}

          {/* Staff Payout Roster Table */}
          <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
            {loadingPayoutSummary ? (
              <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3 cht-text-gray-400">
                <RefreshCw size={32} className="cht-animate-spin cht-text-[#3e57da]" />
                <p className="cht-font-medium cht-text-sm">Calculating monthly payroll records...</p>
              </div>
            ) : !payoutSummary?.staff_roster || payoutSummary.staff_roster.length === 0 ? (
              <div className="cht-py-20 cht-text-center cht-space-y-4">
                <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-text-gray-400 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto">
                  <Wallet size={28} />
                </div>
                <div>
                  <h3 className="cht-text-lg cht-font-black cht-text-gray-800">
                    No Staff Activity for {formatMonth(selectedPayoutMonth)}
                  </h3>
                  <p className="cht-text-gray-400 cht-text-sm cht-mt-1">
                    No jobs logged or commissions accrued for workers in this calendar month.
                  </p>
                </div>
              </div>
            ) : (
              <div className="cht-overflow-x-auto">
                <table className="cht-w-full cht-text-left cht-border-collapse">
                  <thead>
                    <tr className="cht-border-b cht-border-gray-100/80 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      <th className="cht-py-4 cht-px-6">Staff Member</th>
                      <th className="cht-py-4 cht-px-6">Month Jobs</th>
                      <th className="cht-py-4 cht-px-6">Approved Jobs</th>
                      <th className="cht-py-4 cht-px-6">Total Approved</th>
                      <th className="cht-py-4 cht-px-6">Disbursed</th>
                      <th className="cht-py-4 cht-px-6">Pending Payout</th>
                      <th className="cht-py-4 cht-px-6">Status</th>
                      <th className="cht-py-4 cht-px-6 cht-text-right">Payroll Action</th>
                    </tr>
                  </thead>
                  <tbody className="cht-divide-y cht-divide-gray-50">
                    {payoutSummary.staff_roster.map((staff) => {
                      const pendingVal = parseFloat(staff.pending_payout || "0");
                      const earnedVal = parseFloat(staff.total_earned || "0");
                      const paidVal = parseFloat(staff.total_paid || "0");

                      return (
                        <tr key={staff.staff_id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                          <td className="cht-py-4 cht-px-6">
                            <div className="cht-font-black cht-text-sm cht-text-gray-900">
                              {staff.staff_name}
                            </div>
                            <div className="cht-text-xs cht-text-gray-400 cht-font-medium">
                              {staff.staff_email || `Staff ID: ${staff.staff_id}`}
                            </div>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-bold cht-text-sm cht-text-gray-800">
                              {staff.total_jobs} jobs
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-bold cht-text-sm cht-text-emerald-700">
                              {staff.approved_jobs_count} approved
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-black cht-text-sm cht-text-[#3e57da]">
                              ₦{earnedVal.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-bold cht-text-sm cht-text-emerald-600">
                              ₦{paidVal.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            <span className="cht-font-black cht-text-sm cht-text-indigo-700">
                              ₦{pendingVal.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="cht-py-4 cht-px-6">
                            {staff.payout_status === "FULLY_PAID" ? (
                              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 cht-rounded-full cht-text-xs cht-font-black">
                                <Check size={12} /> Settled
                              </span>
                            ) : staff.payout_status === "PENDING_PAYOUT" ? (
                              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200 cht-rounded-full cht-text-xs cht-font-bold">
                                <Clock size={12} /> Ready for Payout
                              </span>
                            ) : (
                              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-gray-100 cht-text-gray-600 cht-rounded-full cht-text-xs">
                                No Commission
                              </span>
                            )}
                          </td>

                          <td className="cht-py-4 cht-px-6 cht-text-right">
                            {pendingVal > 0 ? (
                              <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<Wallet size={14} />}
                                onClick={() => setDisburseModalStaff(staff)}
                                className="cht-rounded-xl"
                              >
                                Disburse Month (₦{pendingVal.toLocaleString("en-NG")})
                              </Button>
                            ) : earnedVal > 0 ? (
                              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-emerald-600 cht-font-bold cht-text-xs">
                                <Check size={14} /> Disbursed
                              </span>
                            ) : (
                              <span className="cht-text-gray-400 cht-text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* View: Wholesale Agents Tab */}
      {activeTab === "agents" && (
        <div className="cht-space-y-6">
          <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-6">
            {agents.length === 0 ? (
              <div className="cht-col-span-full cht-py-20 cht-text-center cht-space-y-3">
                <Building size={32} className="cht-text-gray-300 cht-mx-auto" />
                <h4 className="cht-font-black cht-text-gray-800">No Wholesale Agents Registered</h4>
                <p className="cht-text-gray-400 cht-text-xs">Click 'Add Agent' to register external wholesale partners.</p>
              </div>
            ) : (
              agents.map(agent => (
                <GlassCard key={agent.id} className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
                  <div className="cht-flex cht-items-start cht-justify-between">
                    <div>
                      <h3 className="cht-text-lg cht-font-black cht-text-gray-900">{agent.name}</h3>
                      <div className="cht-text-xs cht-text-gray-400 cht-mt-0.5">
                        {agent.email || agent.phone_number || "No direct contact provided"}
                      </div>
                    </div>
                    <span className="cht-px-2.5 cht-py-1 cht-bg-cyan-50 cht-text-cyan-700 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase">
                      Wholesale Agent
                    </span>
                  </div>

                  {/* Price Tiers List */}
                  <div className="cht-space-y-2 cht-pt-2 cht-border-t cht-border-gray-100">
                    <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                      Contracted Agreed Rates
                    </p>
                    {agent.price_tiers && agent.price_tiers.length > 0 ? (
                      <div className="cht-space-y-1.5">
                        {agent.price_tiers.map((pt, i) => (
                          <div key={i} className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-py-1 cht-px-2.5 cht-bg-gray-50 cht-rounded-xl">
                            <span className="cht-font-bold cht-text-gray-700">
                              {serviceRates.find((sr) => sr.code === pt.service_type)?.name || pt.service_type_name || pt.service_type}
                            </span>
                            <span className="cht-font-black cht-text-[#3e57da]">₦{parseFloat(pt.agreed_price).toLocaleString('en-NG')}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="cht-text-xs cht-text-gray-400 cht-italic">Default service rates apply</p>
                    )}
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </div>
      )}

      {/* View: Services & Rates Management */}
      {activeTab === "services" && (
        <div className="cht-space-y-6">
          <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-4 cht-bg-white cht-p-4 sm:cht-p-5 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
            <div>
              <h3 className="cht-text-base cht-font-black cht-text-gray-900">Database Service Offerings & Rate Rules</h3>
              <p className="cht-text-xs cht-text-gray-500">
                Manage registered services, staff base execution commissions, and default pricing in the database.
              </p>
            </div>
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={handleOpenNewServiceModal}
              className="cht-rounded-xl cht-text-xs"
            >
              Add New Service
            </Button>
          </div>

          <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
            <div className="cht-overflow-x-auto">
              <table className="cht-w-full cht-text-left cht-border-collapse">
                <thead>
                  <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50">
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Order</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Service Name</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Code / Identifier</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Base Commission</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Default Price</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Default Cost</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Status</th>
                    <th className="cht-py-4 cht-px-6 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider cht-text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="cht-divide-y cht-divide-gray-50">
                  {serviceRates.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="cht-py-12 cht-text-center cht-text-gray-400 cht-text-xs">
                        No service rates found in database.
                      </td>
                    </tr>
                  ) : (
                    serviceRates.map((sr) => (
                      <tr key={sr.id} className="hover:cht-bg-gray-50/50 cht-transition-colors">
                        <td className="cht-py-4 cht-px-6 cht-text-xs cht-font-bold cht-text-gray-400">
                          #{sr.order}
                        </td>
                        <td className="cht-py-4 cht-px-6">
                          <span className="cht-text-sm cht-font-bold cht-text-gray-900">{sr.name}</span>
                        </td>
                        <td className="cht-py-4 cht-px-6">
                          <span className="cht-font-mono cht-text-xs cht-font-bold cht-text-indigo-600 cht-bg-indigo-50 cht-px-2.5 cht-py-1 cht-rounded-lg">
                            {sr.code}
                          </span>
                        </td>
                        <td className="cht-py-4 cht-px-6">
                          <span className="cht-text-xs cht-font-black cht-text-emerald-700">
                            ₦{parseFloat(sr.base_commission || "0").toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="cht-py-4 cht-px-6 cht-text-xs cht-font-medium cht-text-gray-600">
                          {parseFloat(sr.default_price || "0") > 0
                            ? `₦${parseFloat(sr.default_price || "0").toLocaleString('en-NG', { minimumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td className="cht-py-4 cht-px-6 cht-text-xs cht-font-medium cht-text-gray-600">
                          {parseFloat(sr.default_cost || "0") > 0
                            ? `₦${parseFloat(sr.default_cost || "0").toLocaleString('en-NG', { minimumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td className="cht-py-4 cht-px-6">
                          <button
                            type="button"
                            onClick={() => handleToggleServiceActive(sr)}
                            className={`cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-border cht-transition-all ${
                              sr.is_active
                                ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border-emerald-200 hover:cht-bg-emerald-100"
                                : "cht-bg-gray-100 cht-text-gray-500 cht-border-gray-200 hover:cht-bg-gray-200"
                            }`}
                            title="Click to toggle active status"
                          >
                            <span className={`cht-w-1.5 cht-h-1.5 cht-rounded-full ${sr.is_active ? "cht-bg-emerald-500" : "cht-bg-gray-400"}`} />
                            {sr.is_active ? "Active" : "Disabled"}
                          </button>
                        </td>
                        <td className="cht-py-4 cht-px-6 cht-text-right">
                          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenEditServiceModal(sr)}
                              leftIcon={<Edit2 size={13} />}
                              className="cht-rounded-xl cht-text-xs cht-py-1 cht-px-2.5"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setServiceToDelete(sr)}
                              leftIcon={<Trash2 size={13} />}
                              className="cht-rounded-xl cht-text-xs cht-py-1 cht-px-2.5 cht-text-rose-600 hover:cht-bg-rose-50"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Edit Financials & Commission Modal */}
      <Modal
        isOpen={!!selectedJobForEdit}
        onClose={() => setSelectedJobForEdit(null)}
        title={`Calibrate Job #${selectedJobForEdit?.sn || selectedJobForEdit?.id}`}
      >
        {selectedJobForEdit && (
          <form onSubmit={handleSaveFinancials} className="cht-space-y-4">
            <div className="cht-p-4 cht-bg-slate-50 cht-rounded-2xl cht-space-y-1 cht-text-xs">
              <div className="cht-flex cht-justify-between"><span className="cht-text-gray-500">Customer:</span><span className="cht-font-bold cht-text-gray-900">{selectedJobForEdit.customer_name}</span></div>
              <div className="cht-flex cht-justify-between">
                <span className="cht-text-gray-500">Service:</span>
                <span className="cht-font-bold cht-text-gray-900">
                  {serviceRates.find((sr) => sr.code === selectedJobForEdit.job_type)?.name || selectedJobForEdit.job_type_name || selectedJobForEdit.job_type}
                </span>
              </div>
              <div className="cht-flex cht-justify-between"><span className="cht-text-gray-500">Staff Assigned:</span><span className="cht-font-bold cht-text-gray-900">{selectedJobForEdit.assigned_staff_name || "Unassigned"}</span></div>
              <div className="cht-flex cht-justify-between"><span className="cht-text-gray-500">Status:</span><span className="cht-font-bold cht-text-gray-900">{selectedJobForEdit.status}</span></div>
              <div className="cht-flex cht-justify-between cht-items-center">
                <span className="cht-text-gray-500">Worker Commission:</span>
                <div className="cht-text-right">
                  <span className="cht-font-black cht-text-[#3e57da]">
                    ₦{(parseFloat(editCommission) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </span>
                  {defaultServiceRateCommission > 0 && Math.abs((parseFloat(editCommission) || 0) - defaultServiceRateCommission) > 0.01 && (
                    <span className="cht-text-[10px] cht-font-bold cht-text-amber-600 cht-block">
                      (Customized from DB rate ₦{defaultServiceRateCommission.toLocaleString('en-NG', { minimumFractionDigits: 2 })})
                    </span>
                  )}
                </div>
              </div>
              <div className="cht-flex cht-justify-between cht-items-center">
                <span className="cht-text-gray-500">Operational Cost:</span>
                <div className="cht-text-right">
                  <span className="cht-font-black cht-text-rose-600">
                    ₦{(parseFloat(editJobCost) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </span>
                  {defaultServiceRateCost > 0 && Math.abs((parseFloat(editJobCost) || 0) - defaultServiceRateCost) > 0.01 && (
                    <span className="cht-text-[10px] cht-font-bold cht-text-amber-600 cht-block">
                      (Customized from DB cost ₦{defaultServiceRateCost.toLocaleString('en-NG', { minimumFractionDigits: 2 })})
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Input
              as="select"
              label="Job Execution Status"
              value={editStatus}
              onChange={(e: any) => setEditStatus(e.target.value as JobStatus)}
              options={[
                { label: "Pending", value: "PENDING" },
                { label: "In Progress", value: "IN_PROGRESS" },
                { label: "Completed", value: "COMPLETED" },
                { label: "Cancelled", value: "CANCELLED" },
              ]}
              helperText="Current execution and workflow status of this job."
            />

            <Input
              as="select"
              label="Referral Source / Channel"
              value={editReferralSource}
              onChange={(e: any) => {
                setEditReferralSource(e.target.value);
                if (e.target.value === "STAFF") {
                  setEditAgentProfileId("");
                }
              }}
              options={[
                { label: "Direct (Office / Admin Channel)", value: "DIRECT" },
                { label: "Walk-in (Counter Client)", value: "WALK_IN" },
                { label: "Staff Referral", value: "STAFF" },
              ]}
              helperText={
                editReferralSource === "DIRECT"
                  ? "Direct: Jobs acquired through office, online, or admin marketing."
                  : editReferralSource === "WALK_IN"
                  ? "Walk-in: Regular walk-in clients attending the counter."
                  : "Staff Referral: Jobs brought in personally by the assigned staff member."
              }
            />

            <div className="cht-p-3.5 cht-bg-blue-50/50 cht-border cht-border-blue-100 cht-rounded-2xl cht-space-y-3">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-xs cht-font-bold cht-text-[#3e57da] cht-block">
                    Assigned Wholesale Agent (Partner)
                  </span>
                  <p className="cht-text-[11px] cht-text-gray-500 cht-mt-0.5">
                    A job can be direct or walk-in and brought by an agent.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAgentModalOpen(true)}
                  className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] hover:cht-underline"
                >
                  + Register New Agent
                </button>
              </div>

              <Input
                as="select"
                value={editAgentProfileId}
                onChange={(e: any) => handleAgentChange(e.target.value)}
                options={[
                  { label: "-- None / Direct Client (No Wholesale Agent) --", value: "" },
                  ...agents.map((a) => ({
                    label: `${a.name}${a.phone_number ? ` (${a.phone_number})` : ""}`,
                    value: String(a.id),
                  })),
                ]}
                helperText={
                  editAgentProfileId
                    ? "Wholesale agent assigned. Contracted wholesale rate is auto-applied below."
                    : "Optional: Assign if this job was brought by a wholesale agent."
                }
              />

              {editAgentProfileId && (
                <div>
                  {activeAgentTier && activeAgentTier.agreed_price ? (
                    <div className="cht-flex cht-items-center cht-justify-between cht-p-2.5 cht-bg-white cht-border cht-border-blue-200 cht-rounded-xl cht-text-xs">
                      <div>
                        <span className="cht-text-gray-500 cht-font-medium">
                          Arranged Price for {selectedJobForEdit.job_type}:{" "}
                        </span>
                        <span className="cht-font-black cht-text-[#3e57da]">
                          ₦{parseFloat(activeAgentTier.agreed_price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditAmountPaid(String(activeAgentTier.agreed_price))}
                        className="cht-text-[11px] cht-font-bold cht-text-blue-700 hover:cht-text-blue-800 cht-bg-blue-50 hover:cht-bg-blue-100 cht-px-2.5 cht-py-1 cht-rounded-lg cht-border cht-border-blue-200"
                      >
                        Apply Arranged Price
                      </button>
                    </div>
                  ) : (
                    <div className="cht-p-2.5 cht-bg-amber-50 cht-border cht-border-amber-200 cht-rounded-xl cht-text-[11px] cht-text-amber-800">
                      <span className="cht-font-bold">No arranged rate set yet for {selectedJobForEdit.job_type}.</span> Setting the amount paid below will establish the wholesale price tier for this agent upon saving.
                    </div>
                  )}
                </div>
              )}
            </div>

            <Input
              label="Amount Paid by Client"
              prefixText="₦"
              type="number"
              step="0.01"
              value={editAmountPaid}
              onChange={(e: any) => setEditAmountPaid(e.target.value)}
              placeholder="0.00"
              helperText={
                editAgentProfileId && activeAgentTier?.agreed_price
                  ? `Contracted wholesale price for ${activeAgent?.name || "agent"} is ₦${parseFloat(activeAgentTier.agreed_price).toLocaleString('en-NG', { minimumFractionDigits: 2 })}.`
                  : parseFloat(selectedJobForEdit.amount_paid || "0") === 0
                  ? "Direct client job was logged with ₦0. Set the final amount charged."
                  : "Gross amount charged / paid by client for this job."
              }
              required
            />

            <Input
              as="select"
              label="Client / Agent Payment Status"
              value={editPaymentStatus}
              onChange={(e: any) => setEditPaymentStatus(e.target.value as PaymentStatus)}
              options={[
                { label: "Paid in Full", value: "PAID" },
                { label: "Unpaid / Pending Payment", value: "UNPAID" },
                { label: "Partially Paid", value: "PARTIAL" },
              ]}
              helperText="Indicate whether the agent or client has settled the payment for this job."
            />

            <div>
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5">
                <label className="cht-text-xs cht-font-bold cht-text-gray-700">
                  Job Operational Cost
                </label>
                {defaultServiceRateCost > 0 && (
                  <button
                    type="button"
                    onClick={() => setEditJobCost(String(defaultServiceRateCost))}
                    className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] hover:cht-underline"
                  >
                    Reset to DB Cost (₦{defaultServiceRateCost.toLocaleString("en-NG", { minimumFractionDigits: 2 })})
                  </button>
                )}
              </div>
              <Input
                prefixText="₦"
                type="number"
                step="0.01"
                value={editJobCost}
                onChange={(e: any) => setEditJobCost(e.target.value)}
                placeholder="0.00"
                helperText={`Standard DB operational cost: ₦${defaultServiceRateCost.toLocaleString("en-NG", { minimumFractionDigits: 2 })}. Net Profit = Amount Paid (₦${(parseFloat(editAmountPaid) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}) − Cost (₦${(parseFloat(editJobCost) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}) − Commission (₦${(parseFloat(editCommission) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}) = ₦${((parseFloat(editAmountPaid) || 0) - (parseFloat(editJobCost) || 0) - (parseFloat(editCommission) || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`}
                required
              />
            </div>

            <div>
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5">
                <label className="cht-text-xs cht-font-bold cht-text-gray-700">
                  Assigned Worker Commission
                </label>
                {defaultServiceRateCommission > 0 && (
                  <button
                    type="button"
                    onClick={() => setEditCommission(String(defaultServiceRateCommission))}
                    className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] hover:cht-underline"
                  >
                    Reset to DB Rate (₦{defaultServiceRateCommission.toLocaleString("en-NG", { minimumFractionDigits: 2 })})
                  </button>
                )}
              </div>
              <Input
                prefixText="₦"
                type="number"
                step="0.01"
                value={editCommission}
                onChange={(e: any) => setEditCommission(e.target.value)}
                placeholder="0.00"
                helperText={`Standard DB rate for ${selectedJobForEdit.job_type}: ₦${defaultServiceRateCommission.toLocaleString("en-NG", { minimumFractionDigits: 2 })}. Assign any custom commission as you wish (e.g. ₦250 if staff completed half the job).`}
                required
              />
            </div>

            <div className="cht-flex cht-items-center cht-gap-3 cht-p-3 cht-bg-gray-50 cht-rounded-2xl">
              <input
                type="checkbox"
                id="is_approved"
                checked={editIsApproved}
                onChange={(e) => {
                  setEditIsApproved(e.target.checked);
                  if (!e.target.checked) {
                    setEditIsCommissionPaid(false);
                  }
                }}
                className="cht-w-5 cht-h-5 cht-accent-[#3e57da] cht-rounded"
              />
              <label htmlFor="is_approved" className="cht-text-xs cht-font-bold cht-text-gray-800 cht-cursor-pointer">
                Authorize & Approve Commission Officially
              </label>
            </div>

            <div className="cht-flex cht-items-center cht-gap-3 cht-p-3 cht-bg-indigo-50/60 cht-border cht-border-indigo-100/70 cht-rounded-2xl">
              <input
                type="checkbox"
                id="is_commission_paid"
                checked={editIsCommissionPaid}
                onChange={(e) => {
                  setEditIsCommissionPaid(e.target.checked);
                  if (e.target.checked) {
                    setEditIsApproved(true);
                  }
                }}
                className="cht-w-5 cht-h-5 cht-accent-[#3e57da] cht-rounded"
              />
              <div>
                <label htmlFor="is_commission_paid" className="cht-text-xs cht-font-bold cht-text-gray-800 cht-cursor-pointer cht-block">
                  Commission Disbursed / Paid (Monthly Payroll)
                </label>
                <p className="cht-text-[11px] cht-text-gray-500 cht-mt-0.5">
                  Check if this job's commission has been disbursed to the worker.
                </p>
              </div>
            </div>

            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-4 cht-border-t cht-border-gray-100">
              <Button
                variant="outline"
                onClick={() => setSelectedJobForEdit(null)}
                disabled={costSubmitting}
                className="cht-rounded-2xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={costSubmitting}
                className="cht-rounded-2xl"
              >
                {costSubmitting ? "Saving..." : "Save Financials"}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Monthly Disbursement Confirmation Modal */}
      <Modal
        isOpen={!!disburseModalStaff}
        onClose={() => setDisburseModalStaff(null)}
        title="Authorize Monthly Commission Disbursement"
      >
        {disburseModalStaff && (
          <div className="cht-space-y-4">
            <div className="cht-p-4 cht-bg-slate-50 cht-rounded-2xl cht-space-y-2 cht-text-xs">
              <div className="cht-flex cht-justify-between">
                <span className="cht-text-gray-500">Staff Member:</span>
                <span className="cht-font-black cht-text-gray-900">{disburseModalStaff.staff_name}</span>
              </div>
              <div className="cht-flex cht-justify-between">
                <span className="cht-text-gray-500">Payroll Month:</span>
                <span className="cht-font-bold cht-text-indigo-600">{formatMonth(selectedPayoutMonth)}</span>
              </div>
              <div className="cht-flex cht-justify-between">
                <span className="cht-text-gray-500">Approved Jobs to Disburse:</span>
                <span className="cht-font-bold cht-text-gray-900">{disburseModalStaff.unpaid_job_ids?.length || 0} jobs</span>
              </div>
              <div className="cht-flex cht-justify-between cht-pt-2 cht-border-t cht-border-gray-200">
                <span className="cht-text-gray-700 cht-font-bold">Total Disbursement:</span>
                <span className="cht-font-black cht-text-lg cht-text-[#3e57da]">
                  ₦{parseFloat(disburseModalStaff.pending_payout || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="cht-p-3.5 cht-bg-amber-50 cht-border cht-border-amber-200 cht-rounded-2xl cht-text-xs cht-text-amber-800">
              <p className="cht-font-bold cht-mb-1">Commission Payout Confirmation</p>
              <p>
                Authorizing will officially mark all {disburseModalStaff.unpaid_job_ids?.length || 0} approved jobs as disbursed in the monthly payroll ledger, timestamp the payment with your credentials, and notify the worker.
              </p>
            </div>

            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
              <Button
                variant="outline"
                onClick={() => setDisburseModalStaff(null)}
                disabled={disbursing}
                className="cht-rounded-2xl"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDisburseStaffSubmit}
                disabled={disbursing}
                className="cht-rounded-2xl"
              >
                {disbursing ? "Disbursing..." : `Confirm & Disburse ₦${parseFloat(disburseModalStaff.pending_payout || "0").toLocaleString("en-NG")}`}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Wholesale Agent Modal */}
      <Modal
        isOpen={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        title="Register Wholesale Agent"
      >
        <form onSubmit={handleCreateAgent} className="cht-space-y-4">
          <Input
            label="Agent / Business Name"
            placeholder="e.g. Apex Corporate Services"
            value={newAgentName}
            onChange={(e: any) => setNewAgentName(e.target.value)}
            required
          />

          <Input
            label="Phone Number (Optional)"
            placeholder="e.g. +234 801 234 5678"
            value={newAgentPhone}
            onChange={(e: any) => setNewAgentPhone(e.target.value)}
          />

          <Input
            label="Email Address (Optional)"
            type="email"
            placeholder="e.g. agent@domain.com"
            value={newAgentEmail}
            onChange={(e: any) => setNewAgentEmail(e.target.value)}
          />

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-4 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsAgentModalOpen(false)}
              disabled={agentSubmitting}
              className="cht-rounded-2xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={agentSubmitting || !newAgentName.trim()}
              className="cht-rounded-2xl"
            >
              {agentSubmitting ? "Registering..." : "Register Agent"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── MODAL: DELETE CONFIRMATION PROMPT ─────────────────────────────── */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteModalOpen(false);
            setJobToDelete(null);
          }
        }}
        size="sm"
        icon={<Trash2 size={22} className="cht-text-rose-600" />}
        title="Delete Job Record"
        subtitle="Permanent superadmin job deletion"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setDeleteModalOpen(false);
                setJobToDelete(null);
              }}
              disabled={deleteSubmitting}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmDeleteJob}
              disabled={deleteSubmitting}
              className="cht-rounded-xl cht-text-xs cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white"
            >
              {deleteSubmitting ? "Deleting..." : "Yes, Delete Record"}
            </Button>
          </div>
        }
      >
        <div className="cht-py-2">
          <p className="cht-text-sm cht-text-gray-700 cht-font-medium">
            Are you sure you want to permanently delete job record{" "}
            <span className="cht-font-black cht-text-gray-900">
              #{jobToDelete?.sn || jobToDelete?.id} ("{jobToDelete?.customer_name}")
            </span>?
          </p>
          <p className="cht-text-xs cht-text-gray-400 cht-mt-2">
            This will permanently remove the record from all reports, ledger calculations, and commission payroll.
          </p>
        </div>
      </Modal>

      {/* ── MODAL: NOTIFICATION / STATUS PROMPT ───────────────────────────── */}
      <Modal
        isOpen={!!promptMessage}
        onClose={() => setPromptMessage(null)}
        size="sm"
        icon={
          promptMessage?.type === "success" ? (
            <CheckCircle2 size={22} className="cht-text-emerald-600" />
          ) : promptMessage?.type === "error" ? (
            <AlertCircle size={22} className="cht-text-rose-600" />
          ) : (
            <AlertCircle size={22} className="cht-text-[#3e57da]" />
          )
        }
        title={promptMessage?.title || "Notification"}
        subtitle="Superadmin job notice"
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setPromptMessage(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Dismiss
            </Button>
          </div>
        }
      >
        <p className="cht-text-sm cht-text-gray-700 cht-font-medium cht-py-2">
          {promptMessage?.message}
        </p>
      </Modal>

      {/* ── MODAL: KPI METRIC INFO ────────────────────────────────────────── */}
      <Modal
        isOpen={!!selectedMetricInfo}
        onClose={() => setSelectedMetricInfo(null)}
        size="md"
        icon={
          selectedMetricInfo === "PENDING_JOBS" ? <Clock size={22} className="cht-text-amber-600" /> :
          selectedMetricInfo === "GROSS_REVENUE" ? <DollarSign size={22} className="cht-text-blue-600" /> :
          selectedMetricInfo === "TOTAL_COSTS" ? <Percent size={22} className="cht-text-rose-600" /> :
          selectedMetricInfo === "COMMISSIONS" ? <ShieldCheck size={22} className="cht-text-amber-600" /> :
          selectedMetricInfo === "NET_PROFIT" ? <TrendingUp size={22} className="cht-text-emerald-600" /> :
          undefined
        }
        title={
          selectedMetricInfo === "PENDING_JOBS" ? "Total Pending Jobs" :
          selectedMetricInfo === "GROSS_REVENUE" ? "Gross Revenue" :
          selectedMetricInfo === "TOTAL_COSTS" ? "Total Job Costs" :
          selectedMetricInfo === "COMMISSIONS" ? "Staff Commissions" :
          selectedMetricInfo === "NET_PROFIT" ? "Net Platform Profit" :
          ""
        }
        subtitle={
          selectedMetricInfo === "PENDING_JOBS" ? "Pipeline workload & fulfillment overview" :
          selectedMetricInfo === "GROSS_REVENUE" ? "Total client billings & wholesale receipts" :
          selectedMetricInfo === "TOTAL_COSTS" ? "Direct registry & third-party filing expenses" :
          selectedMetricInfo === "COMMISSIONS" ? "Execution payouts & wholesale referral margins" :
          selectedMetricInfo === "NET_PROFIT" ? "Retained business margin after direct fulfillment" :
          ""
        }
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setSelectedMetricInfo(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="cht-py-2 cht-space-y-4">
          {selectedMetricInfo === "PENDING_JOBS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-amber-50/70 cht-border cht-border-amber-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-amber-800 cht-uppercase cht-tracking-wide">Current View</span>
                  <div className="cht-text-lg cht-font-black cht-text-amber-700">
                    {financials.pendingJobs} pending jobs
                  </div>
                </div>
                <div className="cht-text-right cht-text-xs cht-text-amber-900/80 cht-font-bold">
                  of {financials.totalJobs} total jobs
                  {monthFilter !== "ALL" && (
                    <div className="cht-text-[10px] cht-text-amber-700 cht-font-medium">
                      Month: {formatMonth(monthFilter)}
                    </div>
                  )}
                </div>
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> The count of jobs across all staff members currently in{" "}
                  <span className="cht-font-bold cht-text-amber-700">PENDING</span> or{" "}
                  <span className="cht-font-bold cht-text-indigo-600">IN_PROGRESS</span> status awaiting execution, document submission, or customer handover.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">SuperAdmin Governance</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li>Jobs remain pending until staff upload completed registration/affidavit documents or confirm completion.</li>
                    <li>Staff commissions are kept in pending status until the job is completed and approved by Superadmin.</li>
                    <li>Monitoring pending volume balances staff workload and eliminates fulfillment bottlenecks.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "GROSS_REVENUE" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-blue-50/70 cht-border cht-border-blue-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase cht-tracking-wide">Gross Billed Revenue</span>
                  <div className="cht-text-xl cht-font-black cht-text-gray-900">
                    ₦{(financials.grossRevenue || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-blue-700">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> Cumulative top-line revenue billed across all customer orders and corporate agent registrations for the selected filter period.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Revenue Breakdown</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li><strong>Direct Clients:</strong> Full retail price paid by direct or walk-in customers.</li>
                    <li><strong>Agent Wholesale:</strong> Negotiated wholesale rate contracted under agent pricing tiers.</li>
                    <li>Gross revenue does not yet deduct government portal filing fees or staff execution commissions.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "TOTAL_COSTS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-rose-50/70 cht-border cht-border-rose-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-rose-800 cht-uppercase cht-tracking-wide">Direct Job Costs</span>
                  <div className="cht-text-xl cht-font-black cht-text-rose-700">
                    ₦{(financials.totalCosts || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-rose-700">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> Direct out-of-pocket fulfillment costs paid to external government registries, court registries, and third-party portals to complete customer jobs.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Direct Cost Components</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li><strong>CAC Portal Fees:</strong> Name availability reservations, Business Name registrations, and Company incorporation filing fees.</li>
                    <li><strong>Court Affidavits:</strong> High Court official stamp duties, commissioner for oaths fees, and seal processing.</li>
                    <li><strong>NERD / TIN Verification:</strong> Portal access and regulatory search service fees.</li>
                    <li>Costs are subtracted from gross revenue to determine platform operational gross margin.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "COMMISSIONS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-amber-50/70 cht-border cht-border-amber-200/60 cht-rounded-2xl cht-space-y-2">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-text-amber-800 cht-uppercase cht-tracking-wide">Total Staff Commissions</span>
                  <div className="cht-text-xl cht-font-black cht-text-amber-700">
                    ₦{(financials.totalCommissions || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="cht-flex cht-items-center cht-justify-between cht-pt-1.5 cht-border-t cht-border-amber-200/50 cht-text-xs">
                  <div>Paid: <span className="cht-font-bold cht-text-emerald-700">₦{(financials.paidCommissions || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</span></div>
                  <div>Pending Payout: <span className="cht-font-bold cht-text-indigo-600">₦{(financials.pendingMonthlyPayout || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</span></div>
                </div>
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> The total performance compensation credited to staff members for executing services or referring wholesale agent jobs.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Commission Structure</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li><strong>Execution Commission:</strong> Standard ₦500 execution cut awarded to staff upon completing eligible services (NERD & Affidavit).</li>
                    <li><strong>Referral Margin:</strong> Surplus amount collected above the contracted wholesale agent price tier.</li>
                    <li><strong>Approval & Payout:</strong> Superadmin approves completed jobs, after which commissions are disbursed individually or via the Monthly Payout payroll batch.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "NET_PROFIT" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-emerald-50/70 cht-border cht-border-emerald-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-emerald-800 cht-uppercase cht-tracking-wide">Net Retained Profit</span>
                  <div className="cht-text-xl cht-font-black cht-text-emerald-700">
                    ₦{(financials.netProfit || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-emerald-800">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> The net platform operating margin retained by Codehub Technologies Ltd after deducting direct fulfillment job costs and staff commissions.
                </p>
                <div className="cht-bg-emerald-900/5 cht-p-3.5 cht-rounded-xl cht-border cht-border-emerald-200/70 cht-space-y-2">
                  <p className="cht-font-black cht-text-emerald-900 cht-uppercase cht-text-[10px] cht-tracking-wider">Calculation Formula</p>
                  <div className="cht-p-2.5 cht-bg-white cht-rounded-lg cht-border cht-border-emerald-200 cht-font-mono cht-text-[11px] cht-text-emerald-950 cht-font-bold">
                    Net Profit = Gross Revenue - Total Job Costs - Staff Commissions
                  </div>
                  <p className="cht-text-[11px] cht-text-emerald-900/80">
                    This figure reflects the core job services operating contribution before general overhead expenses (office rent, utility bills, generator fuel) tracked in the Monthly Ledger.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── MODAL: ADD / EDIT SERVICE RATE ────────────────────────────────── */}
      <Modal
        isOpen={isServiceModalOpen}
        onClose={() => {
          if (!serviceSubmitting) {
            setIsServiceModalOpen(false);
            setSelectedServiceForEdit(null);
          }
        }}
        size="md"
        icon={<SlidersHorizontal size={22} className="cht-text-[#3e57da]" />}
        title={selectedServiceForEdit ? `Edit Service: ${selectedServiceForEdit.name}` : "Add New Service Offering"}
        subtitle="Manage service parameters, base commissions, and default rates in database"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setIsServiceModalOpen(false);
                setSelectedServiceForEdit(null);
              }}
              disabled={serviceSubmitting}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleSaveService}
              disabled={serviceSubmitting}
              className="cht-rounded-xl cht-text-xs"
            >
              {serviceSubmitting ? "Saving..." : selectedServiceForEdit ? "Update Service" : "Create Service"}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSaveService} className="cht-space-y-4 cht-py-1">
          <Input
            label="Service Display Name"
            placeholder="e.g. CAC Registration, High Court Affidavit"
            value={serviceFormName}
            onChange={(e: any) => setServiceFormName(e.target.value)}
            required
            helperText="Human-readable title shown across client job logs and reports."
          />

          <Input
            label="Service Code (Unique Identifier)"
            placeholder="e.g. CAC, AFFIDAVIT, TRADEMARK"
            value={serviceFormCode}
            onChange={(e: any) => setServiceFormCode(e.target.value.toUpperCase())}
            required
            disabled={!!selectedServiceForEdit}
            helperText={selectedServiceForEdit ? "Code cannot be changed once created to preserve historical linkages." : "Short uppercase identifier stored in database records."}
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
            <Input
              label="Base Commission (₦)"
              type="number"
              step="0.01"
              value={serviceFormBaseCommission}
              onChange={(e: any) => setServiceFormBaseCommission(e.target.value)}
              helperText="Fixed execution fee earned by staff on completion."
            />
            <Input
              label="Default Retail Price (₦)"
              type="number"
              step="0.01"
              value={serviceFormDefaultPrice}
              onChange={(e: any) => setServiceFormDefaultPrice(e.target.value)}
              helperText="Standard client billing price."
            />
            <Input
              label="Default Filing Cost (₦)"
              type="number"
              step="0.01"
              value={serviceFormDefaultCost}
              onChange={(e: any) => setServiceFormDefaultCost(e.target.value)}
              helperText="Direct registry fee."
            />
          </div>

          <div className="cht-grid cht-grid-cols-2 cht-gap-3 cht-items-center cht-pt-2">
            <Input
              label="Display Order"
              type="number"
              value={serviceFormOrder}
              onChange={(e: any) => setServiceFormOrder(e.target.value)}
              helperText="Position in selection dropdowns."
            />

            <div className="cht-flex cht-items-center cht-gap-2.5 cht-pt-3">
              <input
                type="checkbox"
                id="service-is-active"
                checked={serviceFormIsActive}
                onChange={(e) => setServiceFormIsActive(e.target.checked)}
                className="cht-w-4 cht-h-4 cht-rounded cht-text-[#3e57da] cht-border-gray-300 focus:cht-ring-[#3e57da]"
              />
              <label htmlFor="service-is-active" className="cht-text-xs cht-font-bold cht-text-gray-700">
                Active & Selectable in Forms
              </label>
            </div>
          </div>
        </form>
      </Modal>

      {/* ── MODAL: DELETE SERVICE CONFIRMATION ─────────────────────────────── */}
      <Modal
        isOpen={!!serviceToDelete}
        onClose={() => {
          if (!serviceDeleteSubmitting) {
            setServiceToDelete(null);
          }
        }}
        size="sm"
        icon={<Trash2 size={22} className="cht-text-rose-600" />}
        title="Delete Service Offering"
        subtitle="Confirm service deletion"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => setServiceToDelete(null)}
              disabled={serviceDeleteSubmitting}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmDeleteService}
              disabled={serviceDeleteSubmitting}
              className="cht-rounded-xl cht-text-xs cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white"
            >
              {serviceDeleteSubmitting ? "Deleting..." : "Yes, Delete Service"}
            </Button>
          </div>
        }
      >
        <div className="cht-py-2">
          <p className="cht-text-sm cht-text-gray-700 cht-font-medium">
            Are you sure you want to permanently delete service{" "}
            <span className="cht-font-black cht-text-gray-900">
              "{serviceToDelete?.name}" ({serviceToDelete?.code})
            </span>?
          </p>
          <p className="cht-text-xs cht-text-gray-400 cht-mt-2">
            If this service is already referenced by historical jobs, consider disabling it instead of deleting.
          </p>
        </div>
      </Modal>

      {/* ── MODAL: SUPERADMIN LOG JOB (FULL ACCESS) ───────────────────────── */}
      <Modal
        isOpen={isLogJobModalOpen}
        onClose={() => {
          if (!logJobSubmitting) {
            setIsLogJobModalOpen(false);
          }
        }}
        size="lg"
        title="Log Staff Job (SuperAdmin Master)"
        subtitle="Full operational access, direct financial calibration, and commission authorization."
      >
        <form onSubmit={handleLogJobSubmit} className="cht-space-y-4">
          {logJobError && (
            <div className="cht-p-3.5 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-2xl cht-flex cht-items-start cht-gap-2.5 cht-text-rose-700 cht-text-xs">
              <AlertCircle size={16} className="cht-shrink-0 cht-mt-0.5" />
              <div className="cht-font-medium">{logJobError}</div>
            </div>
          )}

          {/* Row 1: Executing Staff & Job Date */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              as="select"
              label="Assigned Executing Staff"
              required
              value={logJobData.assigned_staff}
              onChange={(e: any) => setLogJobData({ ...logJobData, assigned_staff: e.target.value })}
              options={[
                { label: "Select Executing Staff...", value: "" },
                ...staffMembers.map((sm) => ({
                  label: sm.name,
                  value: sm.id,
                })),
              ]}
              helperText="The worker credited with executing this job."
            />

            <Input
              type="date"
              label="Job Date"
              required
              value={logJobData.date}
              onChange={(e: any) => setLogJobData({ ...logJobData, date: e.target.value })}
              helperText="Accepts historical dates for backlog entry."
            />
          </div>

          {/* Row 2: Customer Name & Service Type */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Customer / Client Name"
              placeholder="e.g. John Doe / First Bank Plc"
              required
              value={logJobData.customer_name}
              onChange={(e: any) => setLogJobData({ ...logJobData, customer_name: e.target.value })}
            />

            <Input
              as="select"
              label="Service Type"
              required
              value={logJobData.job_type}
              onChange={(e: any) => handleLogJobTypeChange(e.target.value)}
              options={(dynamicServiceTypes || []).map((st) => ({
                label: st.label,
                value: st.value,
              }))}
            />
          </div>

          {/* Row 3: Referral Channel & Agent Selection */}
          <div className="cht-space-y-2">
            <label className="cht-block cht-text-xs cht-font-bold cht-text-[#1e293b]">
              Referral Channel
            </label>
            <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-4 cht-gap-2">
              {[
                { id: "DIRECT", label: "Direct", hint: "₦0 Paid" },
                { id: "WALK_IN", label: "Walk-in", hint: "Retail Client" },
                { id: "STAFF", label: "Staff", hint: "Referral Cut" },
                { id: "AGENT", label: "Wholesale Agent", hint: "Agreed Rate" },
              ].map((rf) => {
                const isSelected = logJobData.referral_source === rf.id;
                return (
                  <button
                    type="button"
                    key={rf.id}
                    onClick={() => handleLogJobReferralChange(rf.id as any)}
                    className={`cht-p-2.5 cht-rounded-2xl cht-border cht-text-left cht-transition-all ${
                      isSelected
                        ? "cht-bg-[#3e57da]/5 cht-border-[#3e57da] cht-text-[#3e57da] cht-shadow-sm"
                        : "cht-bg-gray-50/70 cht-border-gray-200/80 cht-text-gray-600 hover:cht-bg-gray-100"
                    }`}
                  >
                    <div className="cht-font-black cht-text-xs">{rf.label}</div>
                    <div className="cht-text-[10px] cht-text-gray-400 cht-font-medium cht-mt-0.5">{rf.hint}</div>
                  </button>
                );
              })}
            </div>

            {logJobData.referral_source === "AGENT" && (
              <div className="cht-pt-2">
                <Input
                  as="select"
                  label="Select Wholesale Agent"
                  required
                  value={logJobData.agent_profile}
                  onChange={(e: any) => handleLogJobAgentChange(e.target.value)}
                  options={[
                    { label: "Select an Agent...", value: "" },
                    ...agents.map((a) => ({
                      label: `${a.name} (${a.phone_number || a.email || "No contact"})`,
                      value: String(a.id),
                    })),
                  ]}
                  helperText={
                    logJobData.agent_profile
                      ? (() => {
                          const ag = agents.find((a) => String(a.id) === String(logJobData.agent_profile));
                          const tier = ag?.price_tiers?.find((t) => t.service_type === logJobData.job_type);
                          return tier
                            ? `✓ Agreed Wholesale Rate: ₦${parseFloat(tier.agreed_price).toLocaleString("en-NG")} for ${logJobData.job_type}`
                            : `ℹ No custom tier set for ${logJobData.job_type}. Standard rate applies.`;
                        })()
                      : "Select the registered wholesale agent representing this job."
                  }
                />
              </div>
            )}
          </div>

          {/* Row 4: Financial Calibration (Amount Paid, Payment Status, Job Cost) */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
            <Input
              type="number"
              step="any"
              label="Amount Paid (₦)"
              required
              disabled={logJobData.referral_source === "DIRECT"}
              value={logJobData.amount_paid}
              onChange={(e: any) => setLogJobData({ ...logJobData, amount_paid: e.target.value })}
              helperText={logJobData.referral_source === "DIRECT" ? "Direct jobs are logged at ₦0 client revenue." : undefined}
            />

            <Input
              as="select"
              label="Payment Status"
              value={logJobData.payment_status}
              onChange={(e: any) => setLogJobData({ ...logJobData, payment_status: e.target.value as PaymentStatus })}
              options={[
                { label: "Paid in Full", value: "PAID" },
                { label: "Unpaid / Outstanding", value: "UNPAID" },
                { label: "Partial Payment", value: "PARTIAL" },
              ]}
            />

            <Input
              type="number"
              step="any"
              label="Job Operational Cost (₦)"
              value={logJobData.job_cost}
              onChange={(e: any) => setLogJobData({ ...logJobData, job_cost: e.target.value })}
              helperText={
                (() => {
                  const matched = serviceRates.find(s => s.code === logJobData.job_type);
                  const dbCost = matched?.default_cost ? parseFloat(String(matched.default_cost)) : 0;
                  return dbCost > 0
                    ? `Default DB operational cost: ₦${dbCost.toLocaleString("en-NG", { minimumFractionDigits: 2 })}. Adjust if needed.`
                    : "Operational cost incurred by company.";
                })()
              }
            />
          </div>

          {/* Row 5: Live Financial & Audit Breakdown Card */}
          <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-200/80 cht-space-y-2">
            <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-font-black cht-text-gray-500 cht-uppercase cht-tracking-wider">
              <span>Audit Financial Breakdown</span>
              <span className="cht-text-[#3e57da] cht-font-bold cht-capitalize">Live Preview</span>
            </div>

            <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-4 cht-gap-2.5 cht-pt-1">
              <div className="cht-p-2.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100">
                <span className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-block cht-uppercase">Gross Billed</span>
                <span className="cht-text-sm cht-font-black cht-text-gray-900">
                  ₦{parseFloat(logJobData.amount_paid || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="cht-p-2.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100">
                <span className="cht-text-[10px] cht-font-bold cht-text-rose-500 cht-block cht-uppercase">Filing Cost</span>
                <span className="cht-text-sm cht-font-black cht-text-rose-600">
                  ₦{parseFloat(logJobData.job_cost || "0").toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="cht-p-2.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100">
                <span className="cht-text-[10px] cht-font-bold cht-text-indigo-500 cht-block cht-uppercase">Commission</span>
                <span className="cht-text-sm cht-font-black cht-text-indigo-600">
                  ₦{logJobFinancialPreview.commission.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="cht-p-2.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100">
                <span className="cht-text-[10px] cht-font-bold cht-text-emerald-500 cht-block cht-uppercase">Net Profit</span>
                <span className={`cht-text-sm cht-font-black ${logJobFinancialPreview.profit >= 0 ? "cht-text-emerald-600" : "cht-text-rose-600"}`}>
                  ₦{logJobFinancialPreview.profit.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {!logJobFinancialPreview.valid && (
              <div className="cht-p-2.5 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-xl cht-text-rose-700 cht-text-xs cht-font-bold">
                ⚠️ {logJobFinancialPreview.error}
              </div>
            )}
          </div>

          {/* Row 6: SuperAdmin Master Governance Controls (Full Access) */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <div
              onClick={() => setLogJobData({ ...logJobData, is_approved: !logJobData.is_approved })}
              className={`cht-p-3.5 cht-rounded-2xl cht-border cht-cursor-pointer cht-transition-all cht-flex cht-items-start cht-gap-3 ${
                logJobData.is_approved
                  ? "cht-bg-indigo-50/70 cht-border-indigo-200 cht-text-indigo-900"
                  : "cht-bg-gray-50/70 cht-border-gray-200 cht-text-gray-600"
              }`}
            >
              <input
                type="checkbox"
                checked={logJobData.is_approved}
                onChange={() => {}}
                className="cht-mt-0.5 cht-w-4 cht-h-4 cht-rounded cht-text-[#3e57da] cht-border-gray-300 focus:cht-ring-[#3e57da]"
              />
              <div>
                <div className="cht-font-black cht-text-xs cht-flex cht-items-center cht-gap-1.5">
                  <ShieldCheck size={14} className={logJobData.is_approved ? "cht-text-indigo-600" : "cht-text-gray-400"} />
                  Pre-Approve Commission
                </div>
                <div className="cht-text-[11px] cht-text-gray-500 cht-mt-0.5">
                  Authorizes commission entitlement immediately under your master superadmin signature.
                </div>
              </div>
            </div>

            <div
              onClick={() => {
                const nextPaid = !logJobData.is_commission_paid;
                setLogJobData({
                  ...logJobData,
                  is_commission_paid: nextPaid,
                  is_approved: nextPaid ? true : logJobData.is_approved,
                });
              }}
              className={`cht-p-3.5 cht-rounded-2xl cht-border cht-cursor-pointer cht-transition-all cht-flex cht-items-start cht-gap-3 ${
                logJobData.is_commission_paid
                  ? "cht-bg-emerald-50/70 cht-border-emerald-200 cht-text-emerald-900"
                  : "cht-bg-gray-50/70 cht-border-gray-200 cht-text-gray-600"
              }`}
            >
              <input
                type="checkbox"
                checked={logJobData.is_commission_paid}
                onChange={() => {}}
                className="cht-mt-0.5 cht-w-4 cht-h-4 cht-rounded cht-text-emerald-600 cht-border-gray-300 focus:cht-ring-emerald-500"
              />
              <div>
                <div className="cht-font-black cht-text-xs cht-flex cht-items-center cht-gap-1.5">
                  <Wallet size={14} className={logJobData.is_commission_paid ? "cht-text-emerald-600" : "cht-text-gray-400"} />
                  Commission Already Paid Out
                </div>
                <div className="cht-text-[11px] cht-text-gray-500 cht-mt-0.5">
                  Marks commission as already disbursed in monthly payroll (useful for historical backlog).
                </div>
              </div>
            </div>
          </div>

          {/* Row 7: Execution Status & Remark */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
            <div className="sm:cht-col-span-1">
              <Input
                as="select"
                label="Job Status"
                value={logJobData.status}
                onChange={(e: any) => setLogJobData({ ...logJobData, status: e.target.value as JobStatus })}
                options={[
                  { label: "Pending", value: "PENDING" },
                  { label: "In Progress", value: "IN_PROGRESS" },
                  { label: "Completed", value: "COMPLETED" },
                  { label: "Cancelled", value: "CANCELLED" },
                ]}
              />
            </div>

            <div className="sm:cht-col-span-2">
              <Input
                label="Remark / Audit Note (Optional)"
                placeholder="e.g. Expedited filing, client requested certificate copy..."
                value={logJobData.remark}
                onChange={(e: any) => setLogJobData({ ...logJobData, remark: e.target.value })}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              type="button"
              onClick={() => setIsLogJobModalOpen(false)}
              disabled={logJobSubmitting}
              className="cht-rounded-2xl"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={logJobSubmitting || !logJobFinancialPreview.valid}
              className="cht-rounded-2xl cht-shadow-md"
            >
              {logJobSubmitting ? "Logging Job..." : "Log Job as SuperAdmin"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* SuperAdmin Multi-Staff Task Handoff & Split Modal */}
      <Modal
        isOpen={isHandoffModalOpen}
        onClose={() => {
          if (!handoffSubmitting) {
            setIsHandoffModalOpen(false);
            setHandoffTargetJob(null);
          }
        }}
        title="Deliverable Handoff & Commission Split (SuperAdmin)"
      >
        {handoffTargetJob && (
          <form onSubmit={handleSubmitHandoff} className="cht-space-y-4">
            <div className="cht-p-3.5 cht-bg-blue-50/60 cht-border cht-border-blue-100 cht-rounded-2xl">
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1">
                <span className="cht-text-[11px] cht-font-black cht-text-blue-700 cht-uppercase cht-tracking-wider">
                  Target Work Order #{handoffTargetJob.id}
                </span>
                <span className="cht-text-xs cht-font-bold cht-text-gray-600">
                  Current Staff: <strong>{handoffTargetJob.assigned_staff_name || `Staff #${handoffTargetJob.assigned_staff}`}</strong>
                </span>
              </div>
              <p className="cht-text-sm cht-font-bold cht-text-gray-900">
                {handoffTargetJob.customer_name} • {handoffTargetJob.job_type_name || handoffTargetJob.job_type}
              </p>
              <p className="cht-text-xs cht-text-gray-500 cht-mt-0.5">
                Total Deliverable Commission Pool:{" "}
                <strong className="cht-text-[#3e57da]">
                  ₦{(parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </strong>
              </p>
            </div>

            {handoffError && (
              <div className="cht-p-3 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-rose-700">
                {handoffError}
              </div>
            )}

            {handoffSuccess && (
              <div className="cht-p-3 cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-emerald-700">
                {handoffSuccess}
              </div>
            )}

            {/* Successor Specialist Selection */}
            <div>
              <label className="cht-block cht-text-xs cht-font-black cht-text-gray-700 cht-mb-1.5 cht-uppercase cht-tracking-wider">
                Select Successor Specialist <span className="cht-text-rose-500">*</span>
              </label>
              <select
                value={handoffSuccessorId}
                onChange={(e) => setHandoffSuccessorId(e.target.value)}
                className="cht-w-full cht-px-3.5 cht-py-2.5 cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-font-medium cht-text-gray-800 focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-outline-none"
                required
              >
                <option value="">-- Choose Colleague Specialist --</option>
                {staffMembers
                  .filter((s) => s.id !== "ALL" && s.id !== "UNASSIGNED" && String(s.id) !== String(handoffTargetJob.assigned_staff))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (ID: {s.id})
                    </option>
                  ))}
              </select>
            </div>

            {/* Split Presets & Percentage Configuration */}
            <div>
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5">
                <label className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                  Commission Allocation Split
                </label>
                <div className="cht-flex cht-gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(50)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 50
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    50% / 50%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(70)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 70
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    70% / 30%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(30)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 30
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    30% / 70%
                  </button>
                </div>
              </div>

              <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                <div className="cht-p-3 cht-bg-indigo-50/50 cht-border cht-border-indigo-100 cht-rounded-xl">
                  <span className="cht-block cht-text-[10px] cht-font-black cht-text-indigo-800 cht-uppercase cht-tracking-wider cht-mb-1 truncate">
                    {handoffTargetJob.assigned_staff_name || "Predecessor"} Cut
                  </span>
                  <div className="cht-flex cht-items-center cht-gap-1">
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={handoffPredecessorSplit}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(99, parseInt(e.target.value || "0", 10)));
                        setHandoffPredecessorSplit(val);
                        setHandoffSuccessorSplit(100 - val);
                      }}
                      className="cht-w-16 cht-px-2 cht-py-1 cht-bg-white cht-border cht-border-indigo-200 cht-rounded-lg cht-text-sm cht-font-black cht-text-indigo-900 focus:cht-outline-none"
                    />
                    <span className="cht-text-sm cht-font-black cht-text-indigo-900">%</span>
                  </div>
                  <p className="cht-text-xs cht-font-black cht-text-indigo-700 cht-mt-1">
                    ≈ ₦{((parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0) * handoffPredecessorSplit / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="cht-p-3 cht-bg-blue-50/50 cht-border cht-border-blue-100 cht-rounded-xl">
                  <span className="cht-block cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase cht-tracking-wider cht-mb-1 truncate">
                    Successor Specialist Cut
                  </span>
                  <div className="cht-flex cht-items-center cht-gap-1">
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={handoffSuccessorSplit}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(99, parseInt(e.target.value || "0", 10)));
                        setHandoffSuccessorSplit(val);
                        setHandoffPredecessorSplit(100 - val);
                      }}
                      className="cht-w-16 cht-px-2 cht-py-1 cht-bg-white cht-border cht-border-blue-200 cht-rounded-lg cht-text-sm cht-font-black cht-text-blue-900 focus:cht-outline-none"
                    />
                    <span className="cht-text-sm cht-font-black cht-text-blue-900">%</span>
                  </div>
                  <p className="cht-text-xs cht-font-black cht-text-blue-700 cht-mt-1">
                    ≈ ₦{((parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0) * handoffSuccessorSplit / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Handover Reason / Work Accomplished */}
            <div>
              <label className="cht-block cht-text-xs cht-font-black cht-text-gray-700 cht-mb-1.5 cht-uppercase cht-tracking-wider">
                Handover / Split Audit Notes
              </label>
              <textarea
                value={handoffReason}
                onChange={(e) => setHandoffReason(e.target.value)}
                placeholder="Document handoff justification and work fulfilled..."
                className="cht-w-full cht-px-3.5 cht-py-2.5 cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-text-gray-800 focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-outline-none cht-min-h-[70px]"
              />
            </div>

            <div className="cht-pt-2 cht-flex cht-items-center cht-justify-end cht-gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsHandoffModalOpen(false)}
                disabled={handoffSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={handoffSubmitting}
                className="cht-bg-[#3e57da] hover:cht-bg-[#3247b5] cht-text-white"
              >
                {handoffSubmitting ? "Executing Handoff..." : "Confirm Handoff & Split"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminStaffJobs;
